/**
 * RealChatAgentService - Integrates with real backend API
 *
 * Handles two-step agent communication:
 * 1. POST /agent/user-message - Submit prompt and get runtimeSessionId
 * 2. GET /agent/agent-response-stream/{sessionId} - SSE streaming with sequence resumption
 *
 * Uses @microsoft/fetch-event-source for Safari compatibility.
 * Safari has known issues with native fetch + ReadableStream for SSE.
 */

import { RequestRejectedError, isRejectedStatus, rejectedCopy } from './requestRejected';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { regionFailoverService } from './regionFailoverService';
import type { ChatMessage, StreamChunk, SelectorCompletion } from '../types/chat.types';
import type {
  ParentPayloadV3,
  WorkingContext,
} from '../types/parentPayload';
import { getBackendApiUrl } from '../config/backend';
import { ensureLpSession, isLpCartPage, keepLpCartLive, lpCartFromPageHtml, lpCookieMatch, lpCookieSessionForHandoff, prepareLpHandoff, setLpCart } from './lpMonitor';
import { handoffJourney, installJourneyRecorder, onJourneyChange, siteLpForHandoff } from './shopperJourney';
import { logger } from '../utils/logger';
import { parentTargetOrigin } from '../utils/parentOrigin';
import { CARD_TOKEN, safeRedact, splitCardIndices } from '../utils/redactCritical';
import { AGENT_UNAVAILABLE_MESSAGE } from '../constants/humanContact';
import {
  HANDOFF_ATTEMPT_STORAGE_KEY,
  handoffAttemptOpen,
  readHandoffAttempt,
  resumeRetryDelayMs,
  writeHandoffAttempt,
  type HandoffAttempt,
  type HandoffAttemptFields,
} from './handoffAttempt';
import { OPEN_ATTEMPT_STORAGE_KEY, readOpenAttempt } from './handoffOpenAttempt';
import { handoffProviderHeaders, isProdBuild } from './handoffProviderOverride';
import { hoursScopeHeaders } from './liveChatHoursOverride';
import { agentModelHeaders } from './agentModelOverride';
import { consumeFault, playFault } from './streamFaultInjector';
import { clearSessionSyncData } from '../utils/sessionSyncStorage';
import { parseExperimentCode } from '../utils/experimentParser';
import { useChatStore } from '../stores/chatStore';
import { createSessionKeyStore, isSessionNotOwned, registerChatSessionKeys } from './sessionKey';
import { forgetSentPage, previousPageFor, rememberSentPage, tabStorage } from './pageChange';
import { buildPriorConversation, idleSinceLastReply } from './priorConversation';
import { installPageNavigationBridge } from './pageNavigation';
import { buildPageRecord, type PageRecord } from './pageRecord';
import type { HandoffInitiator } from './handoffInitiator';
import { dropStaffSiteTokenIf } from '../utils/staffSiteToken';
import { SECURE_FORM_EVENT, closeSecureForm } from '../components/chat/secureForm/installSecureForm';

// TRAC-732 #18: the agent session + conversation persist in localStorage, which is per-ORIGIN
// and therefore shared by every /embed document — including the dev/testing harnesses (e.g.
// /tr-test simulates being on a real product page and makes the agent "context aware"). Because
// the keys were global, a session/conversation started on a context-simulating test surface
// leaked into the plain MAIN page: initSession() resumed the same session (whose server-side
// memory still held the page-context turns) and the old messages reloaded. We now namespace the
// keys per surface via a `?surface=` tag on the /embed iframe src. The main page and real
// external embeds pass no tag → default keys; test harnesses pass one → isolated storage, so
// they can never pollute the production customer experience. Computed at module load from the
// iframe's own URL (synchronous — available before the singleton constructor reads a session).
function _storageNamespace(): string {
  try {
    const s = new URLSearchParams(window.location.search).get('surface');
    const clean = (s || '').replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
    return clean ? `:${clean}` : '';
  } catch { return ''; }
}
const _NS = _storageNamespace();

/**
 * TRAC-1010: where a message carrying the conversation ID may go. The ID is a
 * handle on a customer's chat, so in prod it goes only to an allowlisted Tire Rack
 * parent (parentTargetOrigin, TRAC-998), never '*': a page that embeds /embed
 * without being on the list gets nothing, because the browser drops the message.
 */
function sessionMessageTarget(): string {
  return parentTargetOrigin(import.meta.env.VITE_ENV || 'dev');
}

/**
 * TRAC-790: tell the page-level loader which session ID this chat is actually on,
 * so the ID it placed in Adobe matches the conversation. Cheap and idempotent —
 * the loader ignores anything it already knows.
 */
function reportSessionIdToParent(sessionId: string): void {
  try {
    if (window.parent === window) return;
    window.parent.postMessage(
      { type: 'TRACI_SESSION_ID', source: 'AI-Assistant', sessionId },
      sessionMessageTarget()
    );
  } catch { /* cross-origin or no parent — nothing to report to */ }
}

/**
 * TRAC-790: answer the page's question even when the answer is "nothing".
 *
 * The page needs to know whether this browser already has a conversation before it
 * decides to start one. It used to learn that only by being told an ID, so silence
 * had to mean either "I have none" or "I have not loaded yet", and it could not tell
 * which. It assumed the second and minted a session — which the chat then ignored,
 * because a conversation in progress keeps its own ID. The result was a session
 * created for nothing on the server, and one visit recorded under two IDs.
 *
 * Saying "none" out loud removes the ambiguity, so a session is only ever created
 * when one is genuinely needed.
 */
/**
 * TRAC-790: accept a session the page created after we told it we had none.
 *
 * The page can only mint AFTER hearing our answer, which is necessarily after this
 * frame has loaded — so the seed cannot arrive on the URL the way it used to. Without
 * this it would mint one for its page records, we would mint a different one on the
 * first message, and the visit would end up under two IDs again, which is the whole
 * thing being fixed. Still refuses to overwrite a real conversation.
 */
function acceptSeededSession(sessionId: string, sessionKey?: string): boolean {
  try {
    if (!/^session-[\w-]{8,200}$/.test(sessionId)) return false;
    const raw = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
    if (raw) {
      // A conversation in progress wins. If it IS this seed (the module-load listener
      // took it first), still keep the key that came with it.
      try { if (sessionKey && JSON.parse(raw).sessionId === sessionId) sessionKeys.remember(sessionId, sessionKey); } catch { /* not ours */ }
      return false;
    }
    localStorage.setItem(STORAGE_KEY_AGENT_SESSION, JSON.stringify({ sessionId, active: false }));
    // TRAC-1089: the page minted this session, so it holds the key that proves we own it.
    sessionKeys.remember(sessionId, sessionKey);
    return true;
  } catch {
    return false;
  }
}



/** Are we running inside the page-level shell, which owns session creation? */
function isInsideShell(): boolean {
  try {
    return typeof window !== 'undefined'
      && window.parent !== window
      && new URLSearchParams(window.location.search).get('shell') === '1';
  } catch { return false; }
}

/**
 * Wait for the page to hand us a session, having told it we had none.
 *
 * Bounded, because everything the customer can do here needs an ID and a shell that
 * never answers must not leave the chat unusable. If it lapses we mint, which is the
 * old behaviour and no worse than before.
 */
// How long to wait for the script to supply a session before giving up on it. Only
// reached when something has gone wrong: normally the seed arrives in well under a
// second, and a failure is signalled rather than waited out.
const SEED_WAIT_MS = 6000;

/**
 * Wait for the page to hand us a session, distinguishing the two reasons it might not.
 *
 * "Nobody is there" and "the owner is still working" look identical if all you do is
 * wait, and they call for opposite responses: mint immediately, or keep waiting. So
 * the page states plainly that it owns session creation, and says so before it has
 * anything to hand over.
 *
 *   no claim heard        -> no page script is running. Mint, quickly.
 *   claim, then a seed    -> use it. The normal path.
 *   claim, then a failure -> it tried and could not. Mint, without waiting out a clock.
 *   claim, then silence   -> it died mid-flight. Mint, but only after the long wait.
 */
function waitForSeededSession(): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    // TRAC-1032: declared before `finish` can run. The page's failure report can arrive
    // before this wait starts, and the read-back below finishes at once -- which used to
    // reach for a poll and a timer not yet declared, throw, and reject initSession with
    // "Cannot access 'poll' before initialization". The connect button then showed
    // "couldn't reach a person" for a request it never sent.
    let poll: ReturnType<typeof setInterval> | undefined;
    let seedTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMsg);
      clearInterval(poll);
      clearTimeout(seedTimer);
      resolve(v);
    };
    const onMsg = (e: MessageEvent) => {
      const d = (e.data || {}) as { type?: string; source?: string; sessionId?: string; sessionKey?: string };
      if (d.source !== 'TR-Parent') return;
      if (d.type === 'TRACI_SEED_SESSION' && d.sessionId) {
        acceptSeededSession(d.sessionId, d.sessionKey);
        finish(d.sessionId);
        return;
      }
      if (d.type === 'TRACI_SEED_FAILED') {
        logger.warn('[SESSION_INIT] Page owns session creation and reported failure — minting');
        finish(null);
      }
    };
    window.addEventListener('message', onMsg);
    if (pageReportedFailure) {
      logger.warn('[SESSION_INIT] Page owns session creation and reported failure — minting');
      finish(null);
      return;
    }
    // The seed can also land through the module-load listener, which writes it straight
    // to storage — watch there too rather than assume ours registered first.
    poll = setInterval(() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
        if (raw) finish(JSON.parse(raw).sessionId || null);
      } catch { /* keep waiting */ }
    }, 120);
    seedTimer = setTimeout(() => finish(null), SEED_WAIT_MS);
  });
}

function reportSessionStateToParent(sessionId: string | null): void {
  try {
    if (window.parent === window) return;
    window.parent.postMessage(
      { type: 'TRACI_SESSION_STATE', source: 'AI-Assistant', sessionId: sessionId || null },
      sessionMessageTarget()
    );
  } catch { /* cross-origin or no parent — nothing to report to */ }
}

/**
 * TRAC-790: adopt the session ID the loader minted at page load (passed on the
 * iframe URL as `?sid=`). Only seeds when this browser has no session of its own:
 * a conversation already in progress keeps its ID. Returns the adopted ID, or null.
 */
function adoptPageSuppliedSessionId(): string | null {
  try {
    const sid = new URLSearchParams(window.location.search).get('sid');
    if (!sid || !/^session-[\w-]{8,200}$/.test(sid)) return null;
    if (localStorage.getItem(STORAGE_KEY_AGENT_SESSION)) return null;   // theirs wins
    // Pending (active:false) — same state initSession's own mint would leave it in;
    // it becomes active on the first message.
    localStorage.setItem(STORAGE_KEY_AGENT_SESSION, JSON.stringify({ sessionId: sid, active: false }));
    return sid;
  } catch {
    return null;
  }
}
/**
 * TRAC-1049: how long a shared open-ask record still means "somebody may be in a queue".
 * Nobody waits twenty minutes for a rep; past this the record is a tab that was left mid-wait.
 */
const HANDOFF_OPEN_ATTEMPT_FRESH_MS = 20 * 60 * 1000;

/**
 * TRAC-1212: how long session-init waits for the host's page-context answer so the open
 * can name the page. tirerack.com answers in milliseconds; past this the open is recorded
 * without the page rather than late.
 */
const PAGE_SETTLE_WAIT_MS = 1500;
/** TRACI-848: handoff-events poll cadence. On screen it stays at 2 s so a live rep's reply (and
 *  their typing status, which rides the same poll) is never slower; off screen nobody can see it,
 *  so it backs off and catches up the moment the chat is visible again. */
const HANDOFF_POLL_ON_SCREEN_MS = 2000;
const HANDOFF_POLL_OFF_SCREEN_MS = 5000;
const HANDOFF_POLL_TIMEOUT_MS = 10_000;
// TRACI-1092: after a connect fetch that got no answer, how long the page asks the backend
// whether the handoff started anyway before it apologises, and how often. The backend logged
// the handoff about 1 s after the browser gave up in the prod traces; 12 s leaves room for a
// slow LivePerson ask without keeping a customer waiting much past the normal connect.
const CONNECT_CONFIRM_WINDOW_MS = 12_000;
const CONNECT_CONFIRM_INTERVAL_MS = 1_500;
const CONNECT_CONFIRM_CALL_TIMEOUT_MS = 4_000;

const STORAGE_KEY_MESSAGES = 'traci_chat_messages' + _NS;
const STORAGE_KEY_AGENT_SESSION = 'traci_agent_session' + _NS;
// TRACI-431: the page this tab's last message was sent from (sessionStorage, per tab).
const STORAGE_KEY_LAST_SENT_PAGE = 'traci_last_sent_page' + _NS;
// TRAC-1089: the ownership key for each recent session (see sessionKey.ts).
const sessionKeys = createSessionKeyStore('traci_agent_session_keys' + _NS);
registerChatSessionKeys(sessionKeys);

// Recorded as it arrives: the page can report a failure while the app is still
// booting, long before anything is waiting to hear it.
let pageReportedFailure = false;

if (typeof window !== 'undefined' && window.parent !== window) {
  // Answer the page the moment this module loads, not when the app has finished
  // booting. The answer is one localStorage read and needs nothing else, but it used
  // to come from initSession — which runs after the React app is up and the token is
  // set, seconds later. The page gave up waiting before we ever spoke, so every fresh
  // visit took the timeout path: it asked, heard nothing, and minted anyway. Asking
  // achieved nothing except the delay.
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
    let existing: string | null = null;
    if (raw) { try { existing = JSON.parse(raw).sessionId || null; } catch { existing = null; } }
    window.parent.postMessage(
      { type: 'TRACI_SESSION_STATE', source: 'AI-Assistant', sessionId: existing },
      sessionMessageTarget()
    );
  } catch { /* no storage, no parent — the page falls back to minting */ }

  window.addEventListener('message', (e: MessageEvent) => {
    const d = (e.data || {}) as { type?: string; source?: string; sessionId?: string; sessionKey?: string };
    if (d.source !== 'TR-Parent') return;
    // These land while the app is still booting, long before anything is waiting on
    // them. Remembered here so the wait can read what already happened instead of
    // listening for messages that have been and gone — which is what made it fall
    // back to "nobody is there" even when the page had said otherwise.
    if (d.type === 'TRACI_SEED_FAILED') { pageReportedFailure = true; return; }
    if (d.type === 'TRACI_SEED_SESSION' && d.sessionId) acceptSeededSession(d.sessionId, d.sessionKey);
  });
}
const STORAGE_KEY_STREAM = 'traci_chat_stream_state';
const STORAGE_KEY_LAST_SEQUENCE = 'traci_last_event_sequence'; // TRAC-203: persists across stream resets
const STORAGE_KEY_HANDOFF_CURSOR = 'traci_handoff_cursor' + _NS; // TRAC-732 #15: last handoff event index the customer saw (survives reload; #18: per-surface)
const STORAGE_KEY_HANDOFF_INTENT = 'traci_handoff_intent' + _NS; // TRAC-732: the customer's latest numbered handoff intent {seq, wantsHuman} — source of truth for connect-vs-leave races (survives reload)
const STORAGE_KEY_HANDOFF_ATTEMPT = HANDOFF_ATTEMPT_STORAGE_KEY + _NS; // TRAC-1006: the handoff attempt, per session, per tab (sessionStorage) — see handoffAttempt.ts

/**
 * How long the SSE reader tolerates NO activity before declaring the stream dead
 * (TRAC-850's circuit breaker fires on this).
 *
 * 45s — cut from 90s in TRAC-862. Two properties make the number load-bearing:
 * this is a *no-activity* timer, and the v2 agent yields its entire answer in a
 * single `chunk` event. With no token-level streaming, the whole turn is one
 * silent gap, so the threshold has to clear real end-to-end turn latency rather
 * than time-to-first-token.
 *
 * Measured over 590 real turns on pr-1365-dherthoge (7 days):
 *   p50 4.6s · p95 12.7s · p99 17.1s · max 19.2s
 *
 * 45s is ~2.3x the worst turn observed in a week, which leaves headroom for an
 * AgentCore cold start (instance churn is real — we saw 7+ boots in 8s during
 * the 2026-07-31 incident). Do not drop this further without the heartbeat: the
 * keepalive is what lets the watchdog measure liveness instead of total latency.
 *
 * Exported so both reader loops and the tests agree on one value — they used to
 * carry independent literals, which is exactly how one gets fixed and one
 * doesn't.
 */
export const STREAM_IDLE_TIMEOUT_MS = 45_000;

/**
 * TRAC-300 client-error reports, at most one per error type per
 * CLIENT_ERROR_REPORT_WINDOW_MS per tab.
 *
 * Each report is its own request, so a failing stream sent two requests per
 * cycle (the GET and this POST), and that pair is what trips the firewall's
 * per-address limits. 26-29 Sep 2026: one stale tab kept one customer's address
 * blocked for three days. The Datadog count (traci.frontend.sse_error) now
 * reads as "tabs having this problem each minute" rather than "attempts", so
 * one broken tab cannot inflate it and a real outage still shows as many tabs.
 * Both idle-timeout sites share the `sse_timeout` key on purpose.
 */
export const CLIENT_ERROR_REPORT_WINDOW_MS = 60_000;
const clientErrorLastReportedAt = new Map<string, number>();

/** Tests that share one module instance start each case with no reports sent. */
export function resetClientErrorReportsForTests(): void {
  clientErrorLastReportedAt.clear();
}

function reportClientError(type: string): void {
  const now = Date.now();
  const last = clientErrorLastReportedAt.get(type);
  if (last !== undefined && now - last < CLIENT_ERROR_REPORT_WINDOW_MS) return;
  clientErrorLastReportedAt.set(type, now);
  fetch(`${getBackendApiUrl()}/metrics/client-error`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type }),
  }).catch(() => {});
}

/**
 * Events that prove the SOCKET is open but say nothing about whether the agent
 * is still working on the turn.
 *
 * TRAC-862 — this set is the difference between a watchdog that works and one
 * that can't fire. The backend emits `heartbeat` every 30s whenever its event
 * queue is idle, which includes the entire time an agent is hung or dead. Those
 * frames were queued like any other event and woke the reader, resetting the
 * idle timer; they were only discarded later, in `convertBackendEventToFrontend`,
 * long after they had already counted as activity.
 *
 * The effect was that the circuit breaker could only ever fire when the whole
 * connection dropped. An agent that died with the socket still warm produced an
 * endless spinner instead — and a 30s heartbeat resets a 45s timer forever, so
 * shortening the timeout alone changes nothing.
 *
 * Drop them at the door. Do NOT add `agent_keepalive` to this set: that one is
 * emitted by the runtime only while a turn is genuinely in flight, and it is
 * the signal the watchdog is supposed to trust.
 */
const CONNECTION_KEEPALIVE_EVENTS = new Set(['heartbeat', 'connected']);

/** Runtime-emitted proof that the current turn is still being worked (TRAC-862). */
export const AGENT_KEEPALIVE_EVENT = 'agent_keepalive';

/**
 * TRAC-531: Collect viewport/screen/locale fields the backend can't derive from headers.
 * Everything here is best-effort — never throws, falls back to undefined.
 */
function collectDeviceHints(): {
  viewport?: { width: number; height: number };
  screen?: { width: number; height: number };
  language?: string;
  timezone?: string;
} {
  const hints: ReturnType<typeof collectDeviceHints> = {};
  try {
    if (typeof window !== 'undefined') {
      if (typeof window.innerWidth === 'number' && typeof window.innerHeight === 'number') {
        hints.viewport = { width: window.innerWidth, height: window.innerHeight };
      }
      if (window.screen && typeof window.screen.width === 'number' && typeof window.screen.height === 'number') {
        hints.screen = { width: window.screen.width, height: window.screen.height };
      }
    }
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      hints.language = navigator.language;
    }
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof tz === 'string') hints.timezone = tz;
    }
  } catch {
    // swallow — device hints are purely diagnostic
  }
  return hints;
}

interface SSEEvent {
  type: string;
  data?: Record<string, unknown>;
  sequence?: number;
}

interface StreamState {
  messageId: string | null;
  contentLength: number;
  lastSequence: number;
  startSequence: number;  // Sequence when this message's response_start was received
  isActive: boolean;
  timestamp: number;
}

interface SendMessageResponse {
  runtimeSessionId: string;
  timestamp: string;
  /** TRACI-863: the backend chose not to answer (a duplicate or mid-handoff return marker). */
  skipped?: string;
  /** TRACI-864: with skipped 'already_answered', the reply an earlier send of this message got. */
  answer?: string;
}

/** TRACI-864: an id for one shopper message, kept across every resend of it. */
function newClientTurnId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

type EventListener = (event: SSEEvent, currentMessageId: string | null) => void;

/**
 * TRAC-291: PDP page context from TR.com postMessage
 */
export interface PageContext {
  tire_name?: string;
  tire_brand?: string;
  tire_model?: string;
  product_size?: string;        // Size on the PDP page: "275/45R21" or "F: 275/45R21 / R: 315/40R21"
  vehicle_size?: string;        // Size that fits the user's vehicle (persists across navigations)
  size_fits_vehicle?: boolean;  // Whether the displayed product_size fits the vehicle
  vehicle_year?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_trim?: string;
  vehicle_requires_runflat?: boolean;  // V2: vehicle's OE spec requires runflat tires
  page_url?: string;
  page_name?: string;
  // TRAC-790: what KIND of page the customer is on — 'TireDetail' / 'TireSearchResults'
  // from TR.com's own payload, or our loader's classification of everything else
  // ('Home', 'Wheels', 'Cart', 'Account', 'Article', 'Other'). Drives the
  // tire-context experience vs. the menu experience.
  page_kind?: string;
  // TRAC-790: the size being SEARCHED on a results page (as opposed to product_size,
  // the size of the tire on a detail page). Normalized through the same builder the
  // agent's working context uses, so the greeting can't contradict the agent.
  search_size?: string;
  adobe_analytics?: Record<string, string>;  // TRAC-465: Adobe Analytics s.* variables from TR.com
}

/**
 * TRAC-291 / TRAC-398: Map a ParentPayloadV3 from TR.com's postMessage to the
 * backend-canonical WorkingContext shape. Pure function — testable in isolation.
 *
 * Defensive guards (web team migration window may ship partial payloads):
 *  - `page_vehicle` is only populated when year + make + model are all truthy.
 *    Backend `ParentVehicleField.model` is required (no `Optional`); a partial
 *    `{year, make}` would 422 the entire request.
 *  - `trim` reads `modelClarifier` first, then legacy `modClar` as a fallback.
 *    The legacy field will be removed after PN1845900.js confirms the rename.
 *
 * Sizes: V3 distinguishes `searchSize` (current page state) from `selectedSize`
 * (user commitment via OE confirmation / By-Size submission). Both flow to
 * the backend; the prompt builder picks which to surface per F1–F8 flag.
 *
 * `chat_vehicle` / `chat_size` are TRAC-397 territory — left null here.
 */
export function buildWorkingContextFromV3(payload: ParentPayloadV3): WorkingContext {
  const v = payload.vehicle;
  // Backend `ParentVehicleField` accepts ONLY {year, make, model, modelClarifier}.
  // `trim` and `requires_runflat` are silently dropped by Pydantic (extra='ignore').
  // Earlier versions of this builder shipped both — the agent's prompt was missing
  // trim info ("SuperCab" etc) for an entire round of ODE testing as a result.
  // Read modelClarifier from V3 with a dual-read shim for legacy `modClar` payloads.
  const modelClarifier =
    v?.modelClarifier ?? (v as { modClar?: string } | undefined)?.modClar ?? null;

  const page_vehicle =
    v && v.year && v.make && v.model
      ? {
          year: String(v.year),
          make: v.make,
          model: v.model,
          modelClarifier,
        }
      : null;

  // Real TR.com payloads ship width/ratio/diameter in inconsistent shapes:
  //   - PDP capture has `width: "265/"`, `ratio: "70"`, `diameter: "17"` (trailing slash on width, no R on diameter)
  //   - Test harnesses sometimes send `width: "265"`, `ratio: "/70"`, `diameter: "R17"` (no slash on width, etc.)
  //   - Some legacy paths send the entire size in `width` only ("215/55R17")
  // Normalize to canonical P-metric: "<width>/<ratio>R<diameter>".
  const normalizeSize = (width: string, ratio?: string, diameter?: string): string => {
    if (!width) return '';
    const w = width.replace(/\/$/, '');
    const r = (ratio || '').replace(/^\//, '');
    const d = (diameter || '').replace(/^R/i, '');
    if (!w) return '';
    const parts = [w];
    if (r) parts.push(`/${r}`);
    if (d) parts.push(`R${d}`);
    return parts.join('');
  };

  const sizeFromV3 = (
    s: ParentPayloadV3['searchSize'] | ParentPayloadV3['selectedSize']
  ): { front: string; rear: string } | null => {
    if (!s) return null;
    const front = normalizeSize(s.width, s.ratio, s.diameter);
    if (!front) return null;
    // Staggered discriminator: real TR.com staggered PDPs (e.g. Tesla Model 3
    // Performance, Audi RS 3) ship rearWidth/rearRatio/rearDiameter on
    // searchSize without setting searchType. The earlier `searchType === 'FR'`
    // gate skipped these and silently mirrored front into rear, which
    // collapsed staggered PDPs to "uniform" in the working-context prose.
    // Detect on full rear-triple presence instead. Backend requires rear
    // (no null), so fall back to front when rear info is absent or partial.
    const rear =
      s.rearWidth && s.rearRatio && s.rearDiameter
        ? normalizeSize(s.rearWidth, s.rearRatio, s.rearDiameter) || front
        : front;
    return { front, rear };
  };

  // page_tire_name: TR.com's V3 tireDetailData doesn't ship a `tireName` key
  // (verified against captures/raw_pdp_michelin_pilot_sport_4s.json — keys are
  // make/model/seoName/sidewall/subPerf/warranties/etc). The canonical display
  // name is `make + " " + model` (e.g., "Michelin Pilot Sport 4S"). Earlier
  // versions used `tireDetailData.tireName ?? null` which always returned null
  // because the field doesn't exist, leaving the [WORKING_CONTEXT] prose
  // without the tire-name signal.
  const td = payload.tireDetailData;
  const tireMake = (td?.make as string | undefined) ?? '';
  const tireModel = (td?.model as string | undefined) ?? '';
  const derivedTireName = `${tireMake} ${tireModel}`.trim();

  return {
    page_type: payload.page ?? null,
    page_tire_name: (td?.tireName as string | undefined) ?? (derivedTireName || null),
    page_vehicle,
    page_searched_size: sizeFromV3(payload.searchSize),
    page_selected_size: sizeFromV3(payload.selectedSize),
    chat_vehicle: null,
    chat_size: null,
  };
}

/**
 * TRAC-291: Extract structured per-page data the agent's `get_page_data` tool reads.
 *
 * Shape MUST match what `agents/tire_agent/src/tools/page_data_core.py` expects:
 *  - tool reads `page_data.get("page")` for page_type discrimination (line 473)
 *  - PLP branch reads `page_data.get("filteredList")` for the tire array (line 488)
 *  - PDP branch reads `tireDetailData`, `instantRebates`, `ratings`, `description`
 *    as **nested** sibling keys at the page_data root (lines 306-312)
 *
 * This shape mirrors Josh's harness fixtures (`fixtures/page_data/*.json`) exactly,
 * which is the contract — earlier versions of this function tried to spread
 * tireDetailData and ship tireList directly, which broke both surfaces in dev
 * testing 2026-05-13.
 *
 * Returns `null` when there's nothing useful to ship.
 */
export function buildPageDataFromV3(payload: ParentPayloadV3): Record<string, unknown> | null {
  if (payload.page === 'TireDetail') {
    if (!payload.tireDetailData || Object.keys(payload.tireDetailData).length === 0) {
      return null;
    }
    const data: Record<string, unknown> = {
      page: payload.page,
      tireDetailData: payload.tireDetailData,
    };
    if (Array.isArray(payload.instantRebates) && payload.instantRebates.length > 0) {
      data.instantRebates = payload.instantRebates;
    }
    if (payload.ratings !== undefined && payload.ratings !== null) {
      data.ratings = payload.ratings;
    }
    if (typeof payload.description === 'string' && payload.description.length > 0) {
      data.description = payload.description;
    }
    return data;
  }
  if (payload.page === 'TireSearchResults') {
    // Tool reads from `filteredList` (Josh's convention). Some TR.com payloads
    // populate `tireList` instead; fall back to it when filteredList is absent.
    const tireArray = (Array.isArray(payload.filteredList) && payload.filteredList.length > 0)
      ? payload.filteredList
      : (Array.isArray(payload.tireList) ? payload.tireList : null);
    if (!tireArray || tireArray.length === 0) {
      return null;
    }

    // Two views of the same data so agent tools can address either scope without
    // confusing them:
    //   filteredList  — the full set of tires TR.com matched for this search
    //                   (read by get_full_search_results for "all"/"any"/"across" queries)
    //   visibleTires  — the slice currently on screen given viewPerPage + startIndex
    //                   (read by get_page_data for "this page"/"these"/"here" queries)
    // TR.com paginates the displayed list client-side from filteredList; visibleTires
    // mirrors that. Re-emits on every page change.
    const startIndex = typeof payload.startIndex === 'number' && payload.startIndex >= 0
      ? payload.startIndex
      : 0;
    const viewPerPage = typeof payload.viewPerPage === 'number' && payload.viewPerPage > 0
      ? payload.viewPerPage
      : tireArray.length;
    const visibleTires = viewPerPage < tireArray.length
      ? tireArray.slice(startIndex, startIndex + viewPerPage)
      : tireArray;

    const data: Record<string, unknown> = {
      page: payload.page,
      filteredList: tireArray,
      visibleTires,
    };
    // The UNFILTERED result set. Preferring filteredList above is right for "what is the
    // customer looking at", but it silently discarded everything their filters excluded:
    // filtered to all-terrain, the agent saw 9 rows of a 118-tire search and could not
    // answer "what else is there?". Ship tireList too whenever it genuinely carries more
    // than the filtered view (equal length = no filter narrowing = no extra information).
    if (Array.isArray(payload.tireList) && payload.tireList.length > tireArray.length) {
      data.tireList = payload.tireList;
    }
    if (Array.isArray(payload.rearTires) && payload.rearTires.length > 0) data.rearTires = payload.rearTires;
    // The size this search is for; plp_facts reads it as `search_size` (TRAC-1072). Without
    // it the agent can only infer the page's size from the rows.
    if (payload.searchSize && typeof payload.searchSize === 'object') data.searchSize = payload.searchSize;
    if (typeof payload.resultCount === 'number') data.resultCount = payload.resultCount;
    if (typeof payload.startIndex === 'number') data.startIndex = payload.startIndex;
    if (typeof payload.viewPerPage === 'number') data.viewPerPage = payload.viewPerPage;
    if (payload.filterParams !== undefined) data.filterParams = payload.filterParams;
    return data;
  }
  return null;
}

/**
 * Parse SSE event from backend
 */
function parseSSEEvent(eventStr: string): SSEEvent | null {
  if (!eventStr.startsWith('data: ')) {
    return null;
  }

  try {
    const jsonStr = eventStr.slice(6); // Remove 'data: ' prefix
    return JSON.parse(jsonStr);
  } catch (e) {
    logger.error('Failed to parse SSE event:', e);
    return null;
  }
}

/**
 * TRAC-732: the live-chat availability answer behind the "Live Chat Available" button.
 * TRAC-965: `failOpen` marks an answer the service made up because the check failed —
 * the button still treats it as open; measurement must not.
 */
export interface LiveChatAvailability {
  state: string;
  hours: string | null;
  wait_seconds: number | null;
  schedule: Array<{ day: string; hours: string }>;
  failOpen?: boolean;
  /** TRACI-435: an admin switched the chat to people only; the AI never answers. */
  livePersonOnly?: boolean;
  /** TRAC-1212: when closed, the reopen moment (ISO 8601 with the ET offset), for the record. */
  nextOpen?: string;
}

/**
 * Shape the backend's availability answer for the button and for measurement. `null`
 * (no usable answer: non-OK, thrown, or no `state`) is the client's own fail-open; a
 * backend answer that says `fail_open` (LivePerson unreachable / creds missing) is
 * carried through as the same flag. Both read as open to the button.
 */
export function toLiveChatAvailability(d: unknown): LiveChatAvailability {
  const o = (d && typeof d === 'object' ? d : null) as Record<string, unknown> | null;
  if (!o || typeof o.state !== 'string' || !o.state) {
    return { state: 'open', hours: null, wait_seconds: null, schedule: [], failOpen: true };
  }
  return {
    state: o.state,
    hours: (typeof o.hours === 'string' && o.hours) ? o.hours : null,
    wait_seconds: typeof o.wait_seconds === 'number' ? o.wait_seconds : null,
    schedule: Array.isArray(o.schedule)
      ? (o.schedule as unknown[]).flatMap((s) => {
          const r = (s && typeof s === 'object' ? s : null) as Record<string, unknown> | null;
          return r && typeof r.day === 'string' && typeof r.hours === 'string'
            ? [{ day: r.day, hours: r.hours }]
            : [];
        })
      : [],
    ...(o.fail_open === true ? { failOpen: true } : {}),
    ...(o.livePersonOnly === true ? { livePersonOnly: true } : {}),
    ...(typeof o.next_open === 'string' && o.next_open ? { nextOpen: o.next_open } : {}),
  };
}

class RealChatAgentService {
  private agentSessionId: string | null;
  // TRAC-732 resume (#15): the event-log cursor to (re)start the handoff poll from. Set by
  // resumeHandoffIfActive() before startHandoffListener so a refresh/navigation catches up from
  // the last event the customer saw instead of replaying the whole handoff. null => from 0.
  private _handoffResumeSince: number | null = null;
  // CPOC-123: the session whose live handoff gets the shopper's journey, and the debounce timer.
  private _journeySession: string | null = null;
  private _journeyTimer: ReturnType<typeof setTimeout> | null = null;
  private streamState: StreamState | null = null;
  private abortController: AbortController | null = null;
  private eventListeners: Map<string, EventListener> = new Map();
  private partnerToken: string | null = null;
  private getTokenFn: (() => Promise<string | undefined>) | null = null;
  private testChunkTimeout: number | null = null; // For testing timeout scenarios (TRAC-81)
  private _pageContext: PageContext | null = null; // TRAC-291: PDP page context (legacy, kept for adobe_analytics + page_name)
  // TRAC-946: the parent page found the tirerack.com-wide internal cookie, so this
  // browser belongs to a team member. Read by the PARENT, never here: this embed is a
  // cross-origin iframe, so any cookie it reads itself is third-party and gets
  // partitioned or blocked. Sticky once seen — the parent re-answers on navigation and
  // a later answer that omits it must not silently demote a known team member.
  private _internalMarker = false;
  private _pageContextListeners: Array<(ctx: PageContext) => void> = []; // TRAC-291: notify on context arrival
  // TRAC-291 / TRAC-398: backend-canonical 5-field working context, built from ParentPayloadV3.
  // Shipped alongside pageContext when content handling is on for the current surface.
  private _workingContext: WorkingContext | null = null;
  // TRACI-431: the host page's URL from its last page-context message (see pageNavigation.ts).
  private _parentPageUrl: string | null = null;
  // TRAC-1212: the host page as the open and every AI reply record it — address, kind and
  // an allow-listed product (pageRecord.ts). Set before content settings gate anything.
  private _pageRecord: PageRecord | null = null;
  // TRACI-910: the opening the shopper was shown (doors to a person or the agent), sent with
  // the message and the handoff request; the backend keeps the first value per chat.
  private _opening: 'liveperson_first' | 'ai_first' | null = null;
  // TRAC-1212: has the host answered (or been given up on) yet? The open is recorded by
  // session-init, which on a fresh visit fires before the host's answer arrives; it waits
  // for this, briefly, so the open can name the page.
  private _pageSettled = false;
  private _pageSettledWaiters: Array<() => void> = [];
  // TRAC-1212: this page load minted a fresh session through session-init, so its open is
  // already on record as agent_session_opened. The open tracker must not count it twice.
  private _mintedSessionThisLoad = false;
  // TRAC-396: one-shot flag set when the user clicks "Browse tires by size".
  // Attached to the next outgoing message's workingContext (browse_by_size_intent)
  // then cleared, so the backend treats that single turn as a browse-by-size request.
  private _browseBySizeIntentNextTurn = false;
  // TRAC-291: structured per-page data (tireDetailData) forwarded to the agent for get_page_data.
  private _pageData: Record<string, unknown> | null = null;
  private _persistenceDegraded = false; // TRAC-848: last saveMessagesToSession failed (quota / storage unavailable)
  // TRAC-518 Phase 8a follow-up: notify React when agentSessionId changes
  // (e.g., initSession() resolves with a pregenerated ID) so consumers can
  // re-render — without this, MessageRenderer never sees the sessionId for
  // the welcome message and starter-prompt impressions don't fire.
  private _agentSessionIdListeners: Array<(id: string | null) => void> = [];
  // TRAC-550 B5: distinct UI state for "the region is flipping under us;
  // we're reopening the SSE on the new region." Separate from B8's per-message
  // retry — that's "Retrying…", this is "Reconnecting…".
  private _isReconnecting: boolean = false;
  private _reconnectingListeners: Array<(reconnecting: boolean) => void> = [];
  private _failoverUnsubscribe: (() => void) | null = null;
  // TRAC-550 B5 follow-up: belt-and-suspenders fallback so the banner can't
  // get permanently stuck. Two stuck-banner modes had no recovery before:
  //   (1) idle flip — pin-flip with no active SSE; nothing to "reopen," so
  //       no onopen ever fires to clear the banner.
  //   (2) failed retry — flip with active SSE → close → reopen attempt
  //       fails permanently; no successful onopen.
  // The active-connection gate (set in the failover subscriber below) fixes
  // (1); this timer is the safety net for (2). 15s is generous: a healthy
  // GA failover + ECS task replacement takes 30-60s in real outages, but
  // the SSE-reopen retry budget is shorter — if we haven't succeeded by
  // then, the banner is doing more harm than good.
  private _reconnectingFallbackTimerId: ReturnType<typeof setTimeout> | null = null;
  private static readonly RECONNECTING_FALLBACK_MS = 15_000;
  // GR-28: caps the GET /agent/agent-response-stream reconnect loop in
  // ensureConnection(). Uncapped, a failing stream retried at a near-fixed
  // ~1s interval forever, and fired one POST /metrics/client-error per
  // attempt — a ~4-minute, ~250-request storm from one shopper's IP got it
  // BlanketRateLimit-blocked in prod on 23 Sep 2026, even though the backend
  // was healthy (no 5xx, normal latency) the whole time.
  private _sseReconnectAttempts = 0;
  // GR-28 (adversarial review): when a connection opens and then errors again
  // almost immediately (an LB or backend that flaps instead of failing to
  // open at all), only a connection that stayed open at least this long earns
  // a fresh retry budget on its next error. Otherwise a flap-then-error cycle
  // would reset the counter to 0 on every open and never hit the cap.
  private _sseConnectionOpenedAt: number | null = null;
  // GR-28 (rework): the budget belongs to a turn, not to one ensureConnection()
  // call. The outer per-message auto-retry (messageRetryScheduler, 4 re-sends)
  // opens a fresh connection per attempt; a per-call reset handed each of them
  // another 6 reconnects against the same broken backend, up to 5x the cap per
  // failing turn. A re-send carries the failing turn's exact text, so only a
  // different message starts a new budget (sendMessageAndStream). Within a turn
  // the budget refills only on evidence of health: a stable connection (above),
  // a completed response, or no SSE error for SSE_BUDGET_REFILL_MS.
  private _sseBudgetTurnMessage: string | null = null;
  // TRACI-864: the message whose reply has not been seen yet, and its clientTurnId. A
  // resend of that same text (the auto-retry, or the shopper retyping it) reuses the id,
  // so the backend answers from the turn it already ran instead of running it again.
  private _unansweredTurn: { key: string; clientTurnId: string } | null = null;
  private _sseLastErrorAt: number | null = null;
  private static readonly SSE_MAX_RECONNECT_ATTEMPTS = 6;
  private static readonly SSE_RECONNECT_BASE_MS = 1_000;
  private static readonly SSE_RECONNECT_MAX_MS = 20_000;
  private static readonly SSE_STABLE_CONNECTION_MS = 10_000;
  private static readonly SSE_BUDGET_REFILL_MS = 120_000;
  // TRAC-1046: the requests a clear fires and nobody awaits — the end-handoff
  // POST and the persistent-session DELETE. A customer has nothing to wait for;
  // the QA extension's New session does, because it destroys this frame the
  // moment the clear returns and a detached frame's in-flight fetch is cancelled.
  // See whenClearSettled().
  private _clearTeardownWork: Array<Promise<unknown>> = [];
  constructor() {
    this.agentSessionId = this.loadAgentSessionId(); // Only loads active sessions

    // TRACI-431: notice the host page moving to a new URL without a reload.
    if (typeof window !== 'undefined' && window.parent !== window) {
      installPageNavigationBridge(window, this, import.meta.env.VITE_ENV || 'dev');
      // CPOC-39 / TRACI-1011: keep this tab's pages and cart for the rep at handoff (every env).
      installJourneyRecorder(import.meta.env.VITE_ENV || 'dev');
      // CPOC-123: during a live handoff, each new page or cart goes to the rep's desk.
      onJourneyChange(() => this._sendJourneySoon());
    }

    // TRAC-279: Capture experiment override early, before auth redirects can strip it
    // If no ?exp= in URL, clear any stale override from a previous test
    if (typeof window !== 'undefined') {
      const expOverride = new URLSearchParams(window.location.search).get('exp');
      if (expOverride) {
        sessionStorage.setItem('traci_experiment_override', expOverride);
      } else {
        sessionStorage.removeItem('traci_experiment_override');
      }
    }

    // Check for test chunk timeout in URL query parameter (TRAC-81)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const testTimeout = urlParams.get('testChunkTimeout');
      if (testTimeout) {
        const timeoutValue = parseFloat(testTimeout);
        if (!isNaN(timeoutValue) && timeoutValue >= 0.1 && timeoutValue <= 90) {
          this.testChunkTimeout = timeoutValue;
          logger.warn(`[TEST MODE] Test chunk timeout enabled: ${timeoutValue}s`);
        }
      }
    }

    // TRAC-276: Expose service on window in dev mode for testing (e.g. corruptPartnerToken)
    // import.meta.env.DEV is tree-shaken out of all builds, so this never exists in deployed environments
    if (import.meta.env.DEV) {
      (window as any).__traci = this;
    }

    // TRAC-550 B5: subscribe to pin-flip events. On a flip, abort any open
    // SSE so the next message lands on the new region; surface "reconnecting"
    // state so the UI can show a distinct indicator (separate from B8 retries).
    //
    // Follow-up: only show the banner when there's actually an active SSE
    // connection at flip time. A flip while the user is idle (between
    // turns) leaves nothing to reopen — onopen never fires for any new
    // SSE — so the previous unconditional `_setReconnecting(true)` left
    // the banner permanently stuck until page refresh.
    this._failoverUnsubscribe = regionFailoverService.subscribe((action) => {
      if (action.type !== "flip") return;
      const hadActiveConnection = this.abortController !== null;
      if (hadActiveConnection) {
        this._setReconnecting(true);
        this.closeActiveConnection();
        // Safety net: if a new SSE never opens (e.g., extended outage past
        // retry budget), clear the banner anyway. A successful onopen will
        // call _setReconnecting(false) sooner, which clears this timer.
        this._reconnectingFallbackTimerId = setTimeout(() => {
          this._reconnectingFallbackTimerId = null;
          if (this._isReconnecting) {
            this._setReconnecting(false);
          }
        }, RealChatAgentService.RECONNECTING_FALLBACK_MS);
      }
      // No active connection → flip is invisible to the user. No banner.
    });
  }

  /**
   * Set the token getter function for Cognito authentication.
   * This function will be called on each request to get a fresh token.
   */
  setTokenGetter(fn: () => Promise<string | undefined>): void {
    this.getTokenFn = fn;
    logger.debug('Token getter configured for Cognito authentication');
    // TRAC-468: Trigger session init as soon as auth is configured.
    // Called from the setter so there's no React timing dependency.
    this.initSession();
  }

  /**
   * Set the partner token for TR.com integration.
   * When set, requests will use Authorization: Bearer header.
   */
  setPartnerToken(token: string): void {
    this.partnerToken = token;
    logger.debug('Partner token configured for TR.com integration');
    // TRAC-468: Trigger session init as soon as auth is configured.
    // Token is already set on `this.partnerToken` so getRequestHeaders() will use it.
    this.initSession();
  }

  /**
   * Check if running in embed mode (partner token)
   */
  isEmbedMode(): boolean {
    return this.partnerToken !== null;
  }

  /**
   * Get the partner token (for DevSettings auth in embed mode)
   */
  getPartnerToken(): string | null {
    return this.partnerToken;
  }

  /**
   * TRAC-291: Set PDP page context from TR.com postMessage.
   *
   * Persists vehicle_size across page navigations: when the page says
   * "this size fits your vehicle", we lock that size as the vehicle's fitted
   * size. On subsequent page navigations (where the displayed size changes),
   * the vehicle's fitted size carries forward — unless the vehicle changes.
   */
  setPageContext(ctx: PageContext): void {
    const prev = this._pageContext;
    this._markPageSettled();

    // TRAC-946: the marker rides in on the parent's context answer, but it
    // describes the browser rather than the page. Lift it off and drop it,
    // so it is reported once as its own thing instead of leaking into
    // page-context metadata where nothing would look for it.
    const marker = (ctx as Record<string, unknown>).internalMarker;
    if (marker === true) this._internalMarker = true;
    delete (ctx as Record<string, unknown>).internalMarker;

    // Carry forward vehicle_size from previous context if:
    // 1. The new context doesn't set its own vehicle_size
    // 2. The vehicle hasn't changed (same year+make+model)
    if (!ctx.vehicle_size && prev?.vehicle_size) {
      const sameVehicle =
        prev.vehicle_year === ctx.vehicle_year &&
        prev.vehicle_make === ctx.vehicle_make &&
        prev.vehicle_model === ctx.vehicle_model;
      if (sameVehicle) {
        ctx.vehicle_size = prev.vehicle_size;
        logger.debug(`[PAGE_CONTEXT] Carried forward vehicle_size=${ctx.vehicle_size} from previous page`);
      }
    }

    this._pageContext = ctx;
    logger.debug('[PAGE_CONTEXT] Set:', ctx);
    // Notify all listeners. Listeners are PERSISTENT (not one-shot): after a
    // Clear-conversation context refresh the parent re-answers, and the
    // one-shot design left nobody listening — the vehicle header chip and
    // contextual welcome stayed dead for the rest of the page's life.
    // Subscribers clean up via the returned unsubscribe (unmount).
    for (const listener of this._pageContextListeners) {
      listener(ctx);
    }
  }

  /**
   * TRAC-291: Subscribe to page context arrival. If context already exists,
   * calls back immediately — and STILL registers, so later refreshes (e.g.
   * after Clear conversation re-requests context) also reach the subscriber.
   */
  onPageContext(listener: (ctx: PageContext) => void): () => void {
    if (this._pageContext) {
      listener(this._pageContext);
    }
    this._pageContextListeners.push(listener);
    return () => {
      this._pageContextListeners = this._pageContextListeners.filter(l => l !== listener);
    };
  }

  /**
   * TRAC-518 Phase 8a follow-up: Subscribe to agentSessionId changes.
   * Fires whenever a session ID becomes available (pregeneration via
   * initSession, or first-message save), or when it is cleared. Consumers
   * should treat the callback as a re-render trigger — React doesn't notice
   * a mutating field on a singleton service otherwise, which is why starter-
   * prompt impressions on the welcome message never fired.
   *
   * Replays the current value immediately if one already exists.
   */
  onAgentSessionIdChange(listener: (id: string | null) => void): () => void {
    if (this.agentSessionId) {
      listener(this.agentSessionId);
    }
    this._agentSessionIdListeners.push(listener);
    return () => {
      this._agentSessionIdListeners = this._agentSessionIdListeners.filter(l => l !== listener);
    };
  }

  private _notifyAgentSessionIdChange(id: string | null): void {
    for (const listener of this._agentSessionIdListeners) {
      try {
        listener(id);
      } catch (e) {
        logger.error('agentSessionId listener error:', e);
      }
    }
  }

  /**
   * TRAC-550 B5: subscribe to "is the SSE currently reconnecting after a
   * region pin-flip?" state. Replays the current value immediately so a
   * late-mounting React component doesn't miss an in-progress reconnect.
   */
  onReconnectingChange(listener: (reconnecting: boolean) => void): () => void {
    listener(this._isReconnecting);
    this._reconnectingListeners.push(listener);
    return () => {
      this._reconnectingListeners = this._reconnectingListeners.filter((l) => l !== listener);
    };
  }

  isReconnecting(): boolean {
    return this._isReconnecting;
  }

  private _setReconnecting(state: boolean): void {
    // Cancel any pending safety-net fallback timer first, regardless of
    // whether the state change is a no-op. A redundant ON→ON transition
    // (back-to-back flips while still reconnecting) needs to drop the old
    // timer so the failover subscriber can arm a fresh one with a renewed
    // budget. A real OFF transition (successful onopen) drops the timer
    // since the banner is already cleared.
    if (this._reconnectingFallbackTimerId !== null) {
      clearTimeout(this._reconnectingFallbackTimerId);
      this._reconnectingFallbackTimerId = null;
    }
    if (this._isReconnecting === state) return;
    this._isReconnecting = state;
    for (const listener of this._reconnectingListeners) {
      try {
        listener(state);
      } catch (e) {
        logger.error("reconnecting listener error:", e);
      }
    }
  }

  /**
   * TRAC-291: Get current page context (if any)
   */
  getPageContext(): PageContext | null {
    return this._pageContext;
  }

  /**
   * TRAC-291 / TRAC-398: set the backend-canonical 5-field working context.
   * Built from ParentPayloadV3 via buildWorkingContextFromV3 below.
   */
  /** TRACI-431: the host page URL, from every page-context message the host sends. */
  setParentPageUrl(url: string | null): void {
    this._parentPageUrl = url;
    // TRAC-1212: a different host address means a different page; the old page's record
    // (its product especially) must not be stamped on the new one's messages.
    if (this._pageRecord && url && this._pageRecord.url !== url) this._pageRecord = null;
    // Not the end of the open's wait: an address alone has no kind and no product. Only a
    // page record ends it (recordHostPage, right after this, or EmbedPage's).
  }

  /**
   * TRAC-1212: build the page record from the host's page-context answer the moment it
   * arrives (the page-navigation bridge calls this). Measurement only, so it does not wait
   * on the content settings the way EmbedPage's handling does. Never throws.
   */
  recordHostPage(data: Record<string, unknown>): void {
    // TRACI-629: the page's cart, for the LivePerson rep; pushed only if this shopper hands
    // off (prepareLpHandoff). loader.js relays it as `lpCart`; tirerack.com's own host sends
    // only the page HTML, where the cart page carries it as an inline lpTag.sdes push.
    // TRACI-852: other pages carry no cart, so the last one seen is kept for a handoff from
    // any page; the cart page without one (emptied) clears it.
    try {
      const cartPage = typeof data.html === 'string' && isLpCartPage(data.url);
      setLpCart(data.lpCart ?? lpCartFromPageHtml(data.html), { cartPage });
    } catch { /* no cart */ }
    try {
      const v3 = data as Partial<ParentPayloadV3>;
      const hasV3 = !!(v3.page || v3.vehicle || v3.searchSize || v3.selectedSize || v3.tireDetailData);
      const working = hasV3 ? buildWorkingContextFromV3(v3 as ParentPayloadV3) : null;
      this.setPageRecord(buildPageRecord(v3, undefined, working));
    } catch {
      /* measurement never breaks the chat */
    }
  }

  /** TRACI-910: the opening ChatInterface drew. Null (no welcome drawn) keeps the last one. */
  setOpening(opening: 'liveperson_first' | 'ai_first' | null): void {
    if (opening) this._opening = opening;
  }

  /** TRAC-1212: the host page's record, built by EmbedPage from the host's context message. */
  setPageRecord(record: PageRecord | null): void {
    this._pageRecord = record;
    this._markPageSettled();
  }

  /**
   * TRAC-1212: the page a record should name. The host's address, never this frame's own
   * (which is the same on every page). Falls back to the bare address when no record was
   * built (a host that sent only a URL).
   */
  getPageRecord(): PageRecord | null {
    const url = this._pageRecord?.url ?? this._pageContext?.page_url ?? this._parentPageUrl ?? undefined;
    if (!this._pageRecord && !url) return null;
    return { ...(this._pageRecord ?? {}), ...(url ? { url } : {}) };
  }

  /** TRAC-1212: whether this page load's session came from a fresh session-init mint. */
  mintedSessionThisLoad(): boolean {
    return this._mintedSessionThisLoad;
  }

  private _markPageSettled(): void {
    if (this._pageSettled) return;
    this._pageSettled = true;
    const waiters = this._pageSettledWaiters;
    this._pageSettledWaiters = [];
    for (const w of waiters) {
      try { w(); } catch { /* measurement never breaks the chat */ }
    }
  }

  /** Resolves when the host has answered, or after `ms` — whichever is first. Never rejects. */
  private waitForPageSettled(ms: number): Promise<void> {
    if (this._pageSettled) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      this._pageSettledWaiters.push(() => { clearTimeout(timer); resolve(); });
    });
  }

  /**
   * TRAC-1212: record that the chat window was opened, for an open session-init does not
   * already record. `kind`: 'remount' — the host created the window again on a new page,
   * with a conversation already under way; 'reshown' — the host showed a window it had
   * hidden on this same page (minimized, then opened again). Recorded as agent_opened,
   * attributed to the host's own button. Fire-and-forget: never throws, never blocks.
   */
  recordChatOpened(kind: 'remount' | 'reshown'): void {
    try {
      const sessionId = this.agentSessionId || this.loadAnySessionId();
      if (!sessionId) return;
      const page = this.getPageRecord();
      const body = JSON.stringify({
        sessionId,
        event: 'opened',
        surface: 'host_button',
        genuine: true,
        reason: kind,
        occurredAt: new Date().toISOString(),
        url: page?.url,
        pageKind: page?.kind,
        product: page?.product,
      });
      void this.getRequestHeaders(sessionId)
        .then((headers) => fetch(`${getBackendApiUrl()}/api/analytics/interaction-events/agent-event`, {
          method: 'POST', headers, body,
        }))
        .catch(() => { /* measurement never breaks the chat */ });
    } catch {
      /* measurement never breaks the chat */
    }
  }

  setWorkingContext(ctx: WorkingContext | null): void {
    this._workingContext = ctx;
    logger.debug('[WORKING_CONTEXT] Set:', ctx);
  }

  getWorkingContext(): WorkingContext | null {
    return this._workingContext;
  }

  /**
   * TRAC-396: mark the NEXT outgoing message as a browse-by-size request.
   * Called when the user clicks the "Browse tires by size" starter. One-shot —
   * the flag is attached to the next send's workingContext, then cleared, so a
   * loosely-formatted size (e.g. "225 65 17") still routes to browse-by-size.
   */
  markBrowseBySizeIntent(): void {
    this._browseBySizeIntentNextTurn = true;
  }

  /**
   * TRAC-291: structured per-page data forwarded to the agent for the
   * get_page_data tool. Built from ParentPayloadV3.tireDetailData (etc.) via
   * buildPageDataFromV3 below.
   */
  setPageData(data: Record<string, unknown> | null): void {
    this._pageData = data;
    logger.debug('[PAGE_DATA] Set:', data ? Object.keys(data) : null);
  }

  getPageData(): Record<string, unknown> | null {
    return this._pageData;
  }


  /**
   * TRAC-276: Handle expired partner token by reloading the page.
   * In embed mode, TR.com serves the iframe URL with a fresh token on each load,
   * so reloading is the simplest way to get a new token. Uses sessionStorage-based
   * loop guard to prevent infinite reloads (max 1 reload per 30s).
   */
  private handleExpiredPartnerToken(pendingMessage?: string): void {
    // CPOC-105: a staff Connect mode token that failed is dropped, so the reload falls
    // back to the site's own token instead of retrying the dead one.
    dropStaffSiteTokenIf(this.partnerToken);
    const GUARD_KEY = 'traci_embed_reload_ts';
    const PENDING_MSG_KEY = 'traci_embed_pending_msg';
    const COOLDOWN_MS = 30_000;
    const lastReload = parseInt(sessionStorage.getItem(GUARD_KEY) || '0', 10);

    if (Date.now() - lastReload < COOLDOWN_MS) {
      logger.warn('[TRAC-276] Token expired but reload cooldown active — skipping');
      return; // fall through to normal error handling
    }

    if (pendingMessage) {
      sessionStorage.setItem(PENDING_MSG_KEY, pendingMessage);
    }

    logger.warn('[TRAC-276] Partner token expired — notifying parent + reloading for fresh token');
    sessionStorage.setItem(GUARD_KEY, Date.now().toString());
    // TRAC-851: in loader/extension embeds the iframe URL's token comes from the
    // PARENT's cache, so reloading alone re-uses the dead token forever (the
    // stuck-"Rolling on it" QA failures). Tell the parent to invalidate its cache
    // and re-mint; it swaps the iframe src with a fresh token. The reload below
    // stays as the fallback for integrations where the host serves a fresh token
    // on each load.
    let parentNotified = false;
    try {
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'TRACI_TOKEN_EXPIRED', source: 'TRACI-Embed' }, '*');
        parentNotified = true;
      }
    } catch { /* cross-origin parent without a handler — reload fallback below */ }
    if (parentNotified) {
      // Give the parent a beat to re-mint and swap the iframe src. If it does,
      // this document is replaced and the timer never fires. Reloading IMMEDIATELY
      // here raced the swap: the embed re-booted on the DEAD token first, and that
      // boot's invalid-token handling wiped the stored session — which is why the
      // conversation came back empty after recovery.
      setTimeout(() => window.location.reload(), 3000);
    } else {
      window.location.reload();
    }
  }

  /**
   * TRAC-276: Check for a pending message saved before a token-expiry reload.
   * Returns and clears the message so it's only sent once.
   */
  consumePendingReloadMessage(): string | null {
    const PENDING_MSG_KEY = 'traci_embed_pending_msg';
    const msg = sessionStorage.getItem(PENDING_MSG_KEY);
    if (msg) {
      sessionStorage.removeItem(PENDING_MSG_KEY);
      logger.info('[TRAC-276] Found pending message from pre-reload — will auto-send');
    }
    return msg;
  }

  /**
   * TRAC-276: Corrupt the partner token for testing the expired-token reload flow.
   * Only useful in dev mode via: __traci.corruptPartnerToken()
   */
  corruptPartnerToken(): void {
    if (this.partnerToken) {
      this.partnerToken = 'ptr_CORRUPTED_FOR_TESTING';
      logger.warn('[TRAC-276] Partner token corrupted for testing');
    }
  }

  /**
   * Set the test chunk timeout for testing timeout scenarios (TRAC-81).
   * This overrides the backend's default 90s chunk read timeout.
   * @param seconds - Timeout in seconds (0.1-90), or null to use default
   */
  setTestChunkTimeout(seconds: number | null): void {
    if (seconds !== null && (seconds < 0.1 || seconds > 90)) {
      logger.warn(`[DEV] Invalid chunk timeout ${seconds}s - must be 0.1-90s`);
      return;
    }
    this.testChunkTimeout = seconds;
    if (seconds !== null) {
      logger.warn(`[DEV] Test chunk timeout set to ${seconds}s`);
    } else {
      logger.debug('[DEV] Test chunk timeout cleared (using default 90s)');
    }
  }

  /**
   * Get the current test chunk timeout setting
   */
  getTestChunkTimeout(): number | null {
    return this.testChunkTimeout;
  }

  /**
   * Get the current health check test / emergency shutoff setting.
   * Returns the current state including who enabled it and when.
   */
  async getHealthCheckTestSetting(): Promise<{
    enabled: boolean;
    enabled_at: string | null;
    enabled_by: string | null;
  }> {
    try {
      const response = await fetch(`${getBackendApiUrl()}/api/settings/health-check-test`, {
        method: 'GET',
        headers: await this.getRequestHeaders(),
      });

      if (!response.ok) {
        logger.error(`Failed to get health check test setting: ${response.status}`);
        return { enabled: false, enabled_at: null, enabled_by: null };
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching health check test setting:', error);
      return { enabled: false, enabled_at: null, enabled_by: null };
    }
  }

  /**
   * Set the health check test / emergency shutoff setting.
   * When enabled:
   * - Health checks return 503 (triggers Datadog alerts)
   * - Agent invocations are blocked for non-Admin/QA users
   *
   * @param enabled - Whether to enable or disable the emergency shutoff
   */
  async setHealthCheckTestSetting(enabled: boolean): Promise<{
    enabled: boolean;
    enabled_at: string | null;
    enabled_by: string | null;
  }> {
    try {
      const response = await fetch(`${getBackendApiUrl()}/api/settings/health-check-test`, {
        method: 'PUT',
        headers: await this.getRequestHeaders(),
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to update setting: ${response.status}`);
      }

      const result = await response.json();
      logger.warn(`[DEV] Agent emergency shutoff ${enabled ? 'ENABLED' : 'DISABLED'}`);
      return result;
    } catch (error) {
      logger.error('Error setting health check test:', error);
      throw error;
    }
  }

  /**
   * Get headers for API requests (handles Cognito and partner token auth)
   */
  /**
   * `sessionId`: the session this request acts on. When we hold its ownership key
   * (TRAC-1089) it rides along; every session-scoped endpoint refuses a call without it.
   */
  private async getRequestHeaders(sessionId?: string | null): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...sessionKeys.headers(sessionId),
    };

    // Priority: Partner token > Cognito token
    if (this.partnerToken) {
      headers['Authorization'] = `Bearer ${this.partnerToken}`;
    } else if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Add test chunk timeout header for timeout testing (TRAC-81)
    if (this.testChunkTimeout !== null && this.testChunkTimeout > 0) {
      headers['X-Test-Chunk-Timeout'] = this.testChunkTimeout.toString();
    }

    // TRAC-1188: the Dev Settings handoff override (never from a prod build).
    Object.assign(headers, handoffProviderHeaders());
    // TRACI-436: the Dev Settings agent model pick (never from a prod build).
    Object.assign(headers, agentModelHeaders());
    // TRACI-555: this browser's own Live chat hours override (never from a prod build).
    Object.assign(headers, hoursScopeHeaders());

    return headers;
  }

  /**
   * Get headers for SSE requests
   */
  private async getStreamHeaders(sessionId?: string | null): Promise<Record<string, string> | undefined> {
    // TRAC-1089: the stream is session-scoped too, so it carries the ownership key.
    const owned = sessionKeys.headers(sessionId);
    // Priority: Partner token > Cognito token
    if (this.partnerToken) {
      return { 'Authorization': `Bearer ${this.partnerToken}`, ...owned };
    } else if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) {
        return { 'Authorization': `Bearer ${token}`, ...owned };
      }
    }
    return Object.keys(owned).length ? owned : undefined;
  }

  /**
   * TRAC-279: Get experiment override from URL or sessionStorage.
   * Captures ?exp= param on first sight and stores it in sessionStorage
   * so it survives auth redirects and SPA navigation.
   */
  private getExperimentOverride(): string | undefined {
    const STORAGE_KEY = 'traci_experiment_override';
    const DEV_STORAGE_KEY = 'traci_dev_experiment_override';

    // Dev panel override has highest priority — normalize to uppercase.
    // A prod build ignores it (TRACI-933): no drawer there to clear a leftover value.
    const devOverride = isProdBuild() ? null : localStorage.getItem(DEV_STORAGE_KEY);
    if (devOverride) {
      const normalized = devOverride.toUpperCase();
      logger.info(`[EXPERIMENT] Override from dev panel: ${normalized}`);
      return normalized;
    }

    // Check URL — new override always wins over stored — normalize to uppercase
    const urlOverride = new URLSearchParams(window.location.search).get('exp');
    if (urlOverride) {
      const normalized = urlOverride.toUpperCase();
      sessionStorage.setItem(STORAGE_KEY, normalized);
      logger.info(`[EXPERIMENT] Override from URL: ?exp=${normalized}`);

      const validPattern = /^([A-Z]\d+)+$/;
      if (!validPattern.test(normalized)) {
        logger.warn(`[EXPERIMENT] Override "${normalized}" has invalid format — expected pairs like "A1B2C3"`);
      }

      return normalized;
    }

    // Fall back to previously captured URL override
    const stored = sessionStorage.getItem(STORAGE_KEY) || undefined;
    if (stored) {
      logger.debug(`[EXPERIMENT] Using stored override: ${stored}`);
    }
    return stored;
  }

  /**
   * Load active agent session ID from storage.
   * Returns null for pending sessions (created by initSession but no message sent yet).
   * Used by validation — pending sessions have no S3 data to validate against.
   */
  loadAgentSessionId(): string | null {
    const stored = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (!data || typeof data.sessionId !== 'string') {
          logger.warn('[SESSION] Invalid session data in storage, clearing');
          localStorage.removeItem(STORAGE_KEY_AGENT_SESSION);
          return null;
        }
        return data.active !== false ? data.sessionId : null;
      } catch (e) {
        logger.error('Failed to parse agent session:', e);
      }
    }
    return null;
  }

  /**
   * Load any session ID from storage, including pending ones.
   * Used by sendMessage to reuse the session ID from initSession.
   */
  private loadAnySessionId(): string | null {
    const stored = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (!data || typeof data.sessionId !== 'string') {
          logger.warn('[SESSION] Invalid session data in storage, clearing');
          localStorage.removeItem(STORAGE_KEY_AGENT_SESSION);
          return null;
        }
        return data.sessionId;
      } catch (e) {
        logger.error('Failed to parse agent session:', e);
      }
    }
    return null;
  }

  /**
   * Save agent session ID to storage as active (message has been sent).
   */
  saveAgentSessionId(sessionId: string): void {
    const previousSessionId = this.agentSessionId;
    this.agentSessionId = sessionId;
    localStorage.setItem(STORAGE_KEY_AGENT_SESSION, JSON.stringify({ sessionId, active: true }));

    if (previousSessionId !== sessionId) {
      logger.debug(`Agent session changed: ${previousSessionId?.substring(0, 12) || 'none'} -> ${sessionId?.substring(0, 12) || 'none'}`);
      this._notifyAgentSessionIdChange(sessionId);
    }

    // TRAC-497: Sync experiment assignments to Zustand store
    this.syncExperimentsToStore(sessionId);
  }

  /**
   * TRAC-497: Parse experiment assignments from session ID and write to Zustand store.
   * Called from saveAgentSessionId and initSession so the store is populated
   * as soon as the session ID is known, eliminating the need for polling.
   */
  private syncExperimentsToStore(sessionId: string): void {
    const assignments = parseExperimentCode(sessionId);
    if (Object.keys(assignments).length > 0) {
      const store = useChatStore.getState();
      store.setExperiments(assignments);
      store.setExperimentsSource('session');
      logger.debug(`[EXPERIMENT] Synced to store: ${Object.entries(assignments).map(([f, v]) => `${f}${v}`).join('-')}`);
    }
  }

  /**
   * TRAC-291: Initialize session ID early (on iframe mount) so experiment
   * assignments are available before the user sends their first message.
   * Only calls backend if no session ID exists yet.
   */
  async initSession(): Promise<string | null> {
    // TRAC-790: the page-level loader now mints the session ID during the page-load
    // handshake and places it in Adobe BEFORE the customer opens anything, so that
    // "shown but never opened" is attributable. It hands the ID down on the iframe
    // URL. Adopt it — but only when this browser has no session of its own, because
    // an in-progress conversation keeps its ID (continuity beats tidiness).
    // Either way the parent is told which ID actually won, so Adobe gets the real one.
    const seeded = adoptPageSuppliedSessionId();
    if (seeded) {
      logger.debug(`[SESSION_INIT] Adopted page-minted session: ${seeded.substring(0, 12)}...`);
    }

    // TRAC-790: inside the page shell, the PAGE is the only thing that creates a
    // session, and this waits for it rather than racing it.
    //
    // Both sides were minting. We told the page we had nothing, the page went off to
    // create one, and in the ~100ms that took we created our own — so the page seeded
    // an ID we then refused, and one visit ended up under two. Two minters cannot be
    // made safe by ordering; there has to be only one.
    //
    // The page wins because it has to record pages for visitors who never open the
    // chat, so it cannot depend on this frame at all. Standalone (no shell) nothing
    // changes: we mint as before, because then there is nobody else to do it.
    if (isInsideShell() && !this.agentSessionId && !this.loadAnySessionId()) {
      // Answer BEFORE waiting, or both sides wait for each other. The page will not
      // create a session until it hears we have none, and we cannot receive one until
      // it does — so waiting first deadlocks, and only the page's own timeout breaks
      // it, two and a half seconds late, on every new visit.
      reportSessionStateToParent(null);
      const seededLate = await waitForSeededSession();
      if (seededLate) {
        this.agentSessionId = seededLate;
        this._notifyAgentSessionIdChange(seededLate);
        reportSessionStateToParent(seededLate);
        logger.debug(`[SESSION_INIT] Took the page's session: ${seededLate.substring(0, 12)}...`);
        return seededLate;
      }
      // No fallback. If the script that put us here cannot supply a session, we stop.
      //
      // There used to be one, and it was worse than useless. It called the same
      // endpoint on the same backend the page had just failed to reach — so in the
      // outage it was written for, it failed too. All it really achieved was hiding
      // that the script was broken, while producing records under an id the script
      // knew nothing about.
      //
      // Failing here is the point. A dead chat gets the script fixed. A chat that
      // limps along makes it look like the script is fine.
      logger.error('[SESSION_INIT] The page script owns session creation and did not supply one. Not starting a session.');
      return null;
    }

    // Check for any existing session (active or pending) to avoid re-calling
    const existingSession = this.agentSessionId || this.loadAnySessionId();
    // Answer either way. "None" is the answer that stops a session being made for
    // nothing, so it matters at least as much as an ID does. (The shell path above
    // has already answered; this covers standalone and the have-one case.)
    if (existingSession) reportSessionStateToParent(existingSession);
    else if (!isInsideShell()) reportSessionStateToParent(null);
    if (existingSession) reportSessionIdToParent(existingSession);
    if (existingSession) {
      logger.debug(`[SESSION_INIT] Already have session: ${existingSession.substring(0, 12)}...`);
      // TRAC-732: ensure the in-memory id is set even when the session was
      // loaded from storage as *pending* (active:false). The constructor uses
      // loadAgentSessionId(), which returns null for pending sessions, so on
      // reload this.agentSessionId is null until set here. escalate/handoff read
      // this.agentSessionId — without this they'd send runtimeSessionId:null → 422.
      if (!this.agentSessionId) {
        this.agentSessionId = existingSession;
        this._notifyAgentSessionIdChange(existingSession);
      }
      return existingSession;
    }

    try {
      return await this.requestSession();
    } catch (err) {
      logger.warn('[SESSION_INIT] Error initializing session:', err);
    }
    return null;
  }

  /**
   * The standalone mint: POST /agent/session-init, stored as pending until the first
   * message, announced to everything keyed off the id. Throws on any failure, tagged the
   * way escalateToHuman tags its own ({ stage: 'session', httpStatus, netError }), so a
   * caller that cannot go on without an id — the connect button — can say which stage
   * broke instead of reporting a session that never existed as the desk refusing.
   *
   * `trigger` says why the session was started (the loader sends 'page_load'); absent,
   * the backend records an ordinary open.
   */
  private async requestSession(trigger?: string): Promise<string> {
    // TRAC-527 follow-up: stamp openedAt at the moment of session creation
    // so the backend uses it as the agent_session_opened trace timestamp.
    // Without this, the trace lands ~roundtrip-latency-ms after the first
    // impression's client-side sortKey, putting agent_session_opened
    // visually behind the welcome impressions in Langfuse.
    const openedAt = new Date().toISOString();
    const expOverride = this.getExperimentOverride();
    const url = expOverride
      ? `${getBackendApiUrl()}/agent/session-init?exp=${expOverride}`
      : `${getBackendApiUrl()}/agent/session-init`;

    // TRAC-401 / TRAC-1212: the HOST page the chat was opened on. This frame's referrer
    // is not it: the browser trims a cross-site referrer to the bare origin, so every
    // open used to read "https://www.tirerack.com/". The host's own address, kind and
    // product arrive on its page-context answer, which on a fresh visit lands a beat
    // after this fires — so wait for it, briefly. Nothing is on screen until that answer
    // (EmbedPage gates the chat on it), so the wait is not one a customer can see.
    // Not for the connect button: a customer is waiting on that one, and the page has
    // long since answered by the time a button can be pressed.
    if (trigger !== 'handoff' && typeof window !== 'undefined' && window.parent !== window) {
      await this.waitForPageSettled(PAGE_SETTLE_WAIT_MS);
    }
    const pageRecord = this.getPageRecord();
    const pageUrl = pageRecord?.url || document.referrer || undefined;

    // TRAC-531: Device context the backend can't derive from request headers
    const deviceHints = collectDeviceHints();

    const startedAt = Date.now();
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          ...await this.getRequestHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pageUrl, openedAt, ...(trigger ? { trigger } : {}), ...deviceHints,
          ...(pageRecord ? { pageRecord } : {}),
        }),
      });
    } catch (netErr) {
      const e = new Error(`Session init network error: ${(netErr as Error)?.message ?? netErr}`);
      Object.assign(e, { stage: 'session', httpStatus: 0, netError: true, elapsedMs: Date.now() - startedAt });
      throw e;
    }
    if (!response.ok) {
      const e = new Error(`Session init failed: ${response.status}`);
      Object.assign(e, { stage: 'session', httpStatus: response.status, elapsedMs: Date.now() - startedAt });
      throw e;
    }
    const data = await response.json();
    const sessionId: string | undefined = data?.runtimeSessionId;
    if (!sessionId) {
      const e = new Error('Session init returned no session id');
      Object.assign(e, { stage: 'session', httpStatus: response.status, elapsedMs: Date.now() - startedAt });
      throw e;
    }

    // TRAC-1089: the key that proves this browser owns the session. Returned only now.
    sessionKeys.remember(sessionId, data?.sessionKey);

    // TRAC-1212: every mint but a page-load one is recorded server-side as an open
    // (agent_session_opened). Set before anyone hears of the id, so the open tracker
    // reads it in time.
    if (trigger !== 'page_load') this._mintedSessionThisLoad = true;

    // Save as pending — session becomes active after first message
    localStorage.setItem(STORAGE_KEY_AGENT_SESSION, JSON.stringify({
      sessionId,
      active: false,
    }));
    // TRAC-518 Phase 8a follow-up: expose the pregenerated ID in-memory
    // so React components can see it before the first message is sent.
    // This is what lets starter-prompt impressions fire on the welcome
    // message — previously the service only updated agentSessionId via
    // saveAgentSessionId (i.e., after first message send).
    if (!this.agentSessionId) {
      this.agentSessionId = sessionId;
      this._notifyAgentSessionIdChange(this.agentSessionId);
    }
    logger.info(`[SESSION_INIT] Session initialized (pending): ${sessionId.substring(0, 30)}...`);
    reportSessionIdToParent(sessionId);

    // TRAC-497: Sync experiment assignments to Zustand store
    this.syncExperimentsToStore(sessionId);

    return sessionId;
  }

  /**
   * Save messages to localStorage
   */
  saveMessagesToSession(messages: ChatMessage[]): void {
    try {
      const serializableMessages = messages.map(msg => ({
        ...msg,
        parts: msg.parts?.map(part => {
          const { ...rest } = part as Record<string, unknown>;
          delete rest.onTireClick;
          delete rest.onActionClick;
          delete rest.onQuickActionClick;
          delete rest.onSlotClick;
          return rest;
        })
      }));
      localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(serializableMessages));
      this._persistenceDegraded = false;
    } catch (e) {
      // TRAC-848: localStorage is ~5MB and a long, card-heavy chat can hit
      // QuotaExceededError here. Before, this only logged: persistence stopped
      // silently and a reload produced a conversation that LOOKED whole. Record
      // it so the export can tell the customer their copy may be incomplete
      // rather than handing them a confidently-truncated file.
      this._persistenceDegraded = true;
      logger.error('Failed to save messages:', e);
    }
  }

  /**
   * TRAC-848: true when the most recent persist attempt failed (quota, private
   * browsing, disabled storage). The export reads this to decide whether to warn.
   * Cleared by the next successful save — a transient failure that later recovers
   * should not permanently brand the conversation as incomplete.
   */
  get persistenceDegraded(): boolean {
    return this._persistenceDegraded;
  }

  /**
   * Load messages from localStorage
   */
  getMessagesFromSession(): ChatMessage[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_MESSAGES);
      if (stored) {
        const messages = JSON.parse(stored);
        return messages.map((msg: Record<string, unknown>) => ({
          ...msg,
          timestamp: new Date(msg.timestamp as string),
          toolCalls: (msg.toolCalls as Record<string, unknown>[] | undefined)?.map((tc: Record<string, unknown>) => ({
            ...tc,
            timestamp: new Date(tc.timestamp as string)
          }))
        }));
      }
    } catch (e) {
      logger.error('Failed to load messages:', e);
    }
    return [];
  }

  /**
   * Close active SSE connection
   */
  closeActiveConnection(): void {
    if (this.abortController) {
      try {
        this.abortController.abort();
        logger.debug('Closed SSE connection');
      } catch (error) {
        logger.error('Error closing SSE connection:', error);
      }
      this.abortController = null;
    }
    // Clear all event listeners
    this.eventListeners.clear();
  }

  /**
   * TRAC-1046: register a request a clear fired and nobody awaits, so a caller
   * that is about to destroy the page can wait for it. Rejections are swallowed
   * here — the waiter cares that the request finished travelling, not that it
   * succeeded, and an unhandled rejection would surface as a console error on
   * every offline clear.
   */
  private _trackClearTeardown<T>(work: Promise<T>): Promise<T> {
    this._clearTeardownWork.push(work.catch(() => undefined));
    return work;
  }

  /**
   * TRAC-1046: resolves once the requests fired by the most recent clear have
   * settled. Used by the QA extension's New session, which tears this frame down
   * as soon as the clear returns — without this wait the end-handoff POST is
   * cancelled mid-flight and the rep's LivePerson conversation is left ringing.
   *
   * Deliberately NOT a general "service is idle" signal: only clear-time
   * teardown requests register, and each waiter drains the queue it was handed,
   * so a second New session is never held up by the first one's requests.
   */
  async whenClearSettled(): Promise<void> {
    const work = this._clearTeardownWork;
    this._clearTeardownWork = [];
    await Promise.all(work);
  }

  /**
   * Clear all storage data
   */
  clearAllStorageData(): void {
    logger.debug(`Clearing all chat data (previous agent session: ${this.agentSessionId?.substring(0, 12) || 'none'})`);
    this.closeActiveConnection();
    localStorage.removeItem(STORAGE_KEY_MESSAGES);
    localStorage.removeItem(STORAGE_KEY_STREAM);
    localStorage.removeItem(STORAGE_KEY_AGENT_SESSION);
    localStorage.removeItem(STORAGE_KEY_LAST_SEQUENCE);
    forgetSentPage(tabStorage(), STORAGE_KEY_LAST_SENT_PAGE);
    this._carryConversation = false; // TRACI-1106: a cleared chat carries nothing forward
    const hadSession = !!this.agentSessionId;
    this.agentSessionId = null;
    this.streamState = null;
    if (hadSession) {
      this._notifyAgentSessionIdChange(null);
    }

    // TRAC-497 note: experiments are deliberately KEPT across a clear. Wiping
    // them hid every experiment-gated surface (e.g. the vehicle header bar,
    // A===2) for the rest of the page's life when nothing re-assigned in-page —
    // and a mid-visit variant flip would contaminate A/B metrics anyway. The
    // next session's real assignment still overwrites via syncExperimentsToStore.

    // TRAC-373: Also clear persistent session from S3 so the agent starts fresh
    // TRAC-1046: tracked, so a caller about to destroy this frame can wait for it.
    this._trackClearTeardown(this.getRequestHeaders().then(headers => {
      return fetch(`${getBackendApiUrl()}/api/session`, {
        method: 'DELETE',
        headers,
      }).then(r => {
        if (r.ok) logger.debug('S3 persistent session cleared');
        else logger.warn(`Failed to clear S3 session: ${r.status}`);
      }).catch(err => logger.warn('Failed to clear S3 session:', err));
    }));

    logger.debug('Chat data cleared - next message will create new agent session');
  }

  /**
   * TRAC-468: Clear ALL user state — the "nuclear option" for the clear button.
   * Handles everything clearAllStorageData does, plus orphaned localStorage keys,
   * sessionStorage keys, session sync data, and in-memory singleton state.
   *
   * Auth state (partnerToken, getTokenFn) is intentionally preserved — that's
   * auth configuration, not session state.
   */
  clearAllUserState(): void {
    // 1. Existing cleanup (localStorage keys, S3 DELETE, SSE close)
    this.clearAllStorageData();

    // 2. Session sync data (traci_session_hash, traci_session_user_id)
    // Imported at module level — clearSessionSyncData is a standalone function
    clearSessionSyncData();

    // 3. Orphaned feedback/survey localStorage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('feedback_') || key.startsWith('survey_draft_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      logger.debug(`[TRAC-468] Cleared ${keysToRemove.length} orphaned feedback/survey keys`);
    }

    // 4. sessionStorage keys from embed and dev tools
    // Note: traci_dev_experiment_override is intentionally preserved —
    // dev overrides should survive session clears so the new session
    // picks up the same variant override.
    sessionStorage.removeItem('traci_experiment_override');
    sessionStorage.removeItem('traci_embed_reload_ts');
    sessionStorage.removeItem('traci_embed_pending_msg');

    // 5. In-memory singleton state (NOT auth — that's config, not session)
    // Clear cached page context but keep _pageContextListeners — those are live
    // React subscriptions that would break the welcome message if dropped.
    // TRAC-694: also null _workingContext + _pageData. They're set from the same
    // postMessage handshake as _pageContext (always paired in EmbedPage) and are
    // sent in the chat request body, so leaving them stale leaks the previous
    // page's context into a cleared session.
    this._pageContext = null;
    this._workingContext = null;
    this._pageData = null;
    // TRAC-396: drop the one-shot browse-by-size flag so a session clear between
    // the "Browse tires by size" click and the next send can't leak the intent
    // onto an unrelated first message.
    this._browseBySizeIntentNextTurn = false;

    logger.debug('[TRAC-468] All user state cleared');
  }

  /**
   * TRAC-203: Persist the last event sequence separately from stream state.
   * This value survives stream completion (isActive=false) and browser restarts,
   * ensuring new messages don't replay old events from the DynamoDB buffer.
   */
  private persistLastSequence(sequence: number): void {
    if (sequence > 0) {
      const prev = localStorage.getItem(STORAGE_KEY_LAST_SEQUENCE);
      const prevNum = prev ? parseInt(prev, 10) || 0 : 0;
      // TRAC-256: Only write if new value is higher. Without this guard,
      // updateStreamState(done) clobbers the correct high-water mark because
      // it falls back to this.streamState.lastSequence (stale — only tracks
      // content chunks) instead of the true last event sequence.
      if (sequence <= prevNum) {
        return;
      }
      localStorage.setItem(STORAGE_KEY_LAST_SEQUENCE, sequence.toString());
      logger.debug(`[TRAC-203] persistLastSequence: ${prev || '(none)'} → ${sequence}`);
    }
  }

  /**
   * TRAC-203: Read the persisted last event sequence.
   * Returns 0 if no sequence has been persisted.
   */
  getPersistedLastSequence(): number {
    const stored = localStorage.getItem(STORAGE_KEY_LAST_SEQUENCE);
    const result = stored ? parseInt(stored, 10) || 0 : 0;
    logger.debug(`[TRAC-203] getPersistedLastSequence: ${result} (raw: ${stored || '(none)'})`);
    return result;
  }

  /**
   * Update stream state for resumption
   * @param isNewMessage - Set to true when starting a new message to reset startSequence
   */
  updateStreamState(messageId: string | null, contentLength: number, isActive: boolean, eventSequence: number | null = null, isNewMessage: boolean = false): void {
    const currentLastSequence = eventSequence ?? this.streamState?.lastSequence ?? 0;

    // TRAC-203 debug logging
    if (isNewMessage || !isActive) {
      logger.debug(`[TRAC-203] updateStreamState: msgId=${messageId?.substring(0, 8) || 'null'} isActive=${isActive} eventSeq=${eventSequence} isNew=${isNewMessage} prevLastSeq=${this.streamState?.lastSequence ?? 'null'} → lastSeq=${currentLastSequence}`);
    }

    // Determine startSequence:
    // - If new message: reset to -1 (will be set on first real event)
    // - If we have a real sequence (> 0) and startSequence is unset (-1 or 0): set it now
    // - Otherwise: keep existing startSequence
    let startSequence: number;
    if (isNewMessage) {
      startSequence = -1;  // Will be set on first real event
    } else if (currentLastSequence > 0 && (this.streamState?.startSequence ?? -1) <= 0) {
      // First real event - capture this as the message's start sequence
      startSequence = currentLastSequence;
    } else {
      startSequence = this.streamState?.startSequence ?? currentLastSequence;
    }

    this.streamState = {
      messageId,
      contentLength,
      lastSequence: currentLastSequence,
      startSequence,
      isActive,
      timestamp: Date.now()
    };

    // TRAC-203: Always persist lastSequence to a separate key so it survives
    // stream completion and browser restarts. Without this, sending a new message
    // after browser restart would have lastSequence=0, causing the backend to
    // replay ALL events (including old messages) from the DynamoDB buffer.
    this.persistLastSequence(currentLastSequence);

    if (isActive) {
      localStorage.setItem(STORAGE_KEY_STREAM, JSON.stringify(this.streamState));
    } else {
      localStorage.removeItem(STORAGE_KEY_STREAM);
    }
  }

  /**
   * Get current stream state
   */
  getStreamState(): StreamState | null {
    if (this.streamState?.isActive) {
      logger.debug(`[TRAC-203] getStreamState: from memory (active) lastSeq=${this.streamState.lastSequence} startSeq=${this.streamState.startSequence}`);
      return this.streamState;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY_STREAM);
      if (stored) {
        const parsed = JSON.parse(stored);
        logger.debug(`[TRAC-203] getStreamState: from localStorage lastSeq=${parsed.lastSequence} startSeq=${parsed.startSequence} isActive=${parsed.isActive}`);
        return parsed;
      }
    } catch (e) {
      logger.error('Failed to load stream state:', e);
    }

    logger.debug(`[TRAC-203] getStreamState: null (no state found)`);
    return null;
  }

  /**
   * Check if there's an active stream
   */
  hasActiveStream(): boolean {
    const state = this.getStreamState();
    if (!state) return false;

    // Consider stream stale if older than 5 minutes
    const age = Date.now() - state.timestamp;
    return age < 5 * 60 * 1000;
  }

  /**
   * Send message to backend to trigger agent processing
   */
  async sendMessage(
    message: string,
    sessionId: string | null = null,
    userId: string | null = null,
    ymmvTimingMs?: { elapsedMs: number; openedAt: number } | null,
    selectorCompletion?: SelectorCompletion | null,
    clientTurnId?: string | null,
  ): Promise<SendMessageResponse> {
    try {
      // Card/SSN scrub at the network boundary — the LAST thing before the prompt
      // is put on the wire to our backend. Any path that reaches here (typed chat,
      // resend, a future direct caller) has payment cards / SSNs removed before the
      // request leaves the browser, so our backend never sees the raw digits.
      // Mirrors the backend redact_critical (shared/pii.py); backend stays the guarantee.
      const typedMessage = message;
      message = safeRedact(message).text; // CPOC-140: fails closed to "[message withheld]"
      const requestSessionId = sessionId || this.agentSessionId || this.loadAnySessionId() || undefined;
      // For embed mode (partner token), don't send userId - let the backend determine it from the token
      const effectiveUserId = this.isEmbedMode() ? undefined : (userId || undefined);
      logger.debug(`Sending message with session: ${requestSessionId?.substring(0, 12) || 'undefined (will create new)'}, user: ${effectiveUserId || '(from auth)'}, embed: ${this.isEmbedMode()}`);

      const body: Record<string, unknown> = {
        prompt: message,
        runtimeSessionId: requestSessionId,
        userId: effectiveUserId,
        ...(clientTurnId ? { clientTurnId } : {}),
        experimentOverride: this.getExperimentOverride(),
      };
      if (ymmvTimingMs) {
        body.ymmvTimingMs = ymmvTimingMs;
      }
      // TRAC-946: only ever sent when true. Absence means "we cannot tell",
      // which the backend reports as unmarked rather than guessing customer.
      if (this._internalMarker) {
        body.internalMarker = true;
      }
      // YMMV picker RESUME (Slice 2): forward the structured pick so the agent resumes the
      // pinned intent + binds the picked vehicle (agentv2.resume). Backend stays a thin forwarder.
      if (selectorCompletion) {
        body.selectorCompletion = selectorCompletion;
      }
      // TRAC-631: ensure every request carries the parent window's URL so
      // Langfuse traces can be tied back to whichever page the user was on.
      // Real cross-origin embeds (TR.com / ODE) get page_url from the parent's
      // TRACI_PAGE_CONTEXT postMessage. Same-origin contexts (the TRACI
      // dashboard, embed-gating-test, etc.) don't post that message, so we
      // fall back to reading `window.parent.location.href` directly — which
      // works precisely because the boundary is same-origin. Cross-origin
      // access throws SecurityError; in that case we fall back to whatever
      // `_pageContext` already has from postMessage (or nothing).
      const mergedPageContext: PageContext = this._pageContext ? { ...this._pageContext } : {};
      if (!mergedPageContext.page_url && typeof window !== 'undefined' && window.parent !== window) {
        try {
          const parentHref = window.parent.location.href;
          if (parentHref) mergedPageContext.page_url = parentHref;
        } catch {
          // Cross-origin block — postMessage flow is the path for those.
        }
      }
      if (Object.keys(mergedPageContext).length > 0) {
        body.pageContext = mergedPageContext;
      }
      // TRAC-1212: the page this message was sent from, for the AI reply's trace.
      const pageRecord = this.getPageRecord();
      if (pageRecord) {
        body.pageRecord = pageRecord;
      }
      if (this._opening) body.opening = this._opening; // TRACI-910
      // TRACI-431: the shopper is on a different page from the one their last message
      // in this conversation was sent from. The backend tells the agent, for this turn.
      // A tire page from TR.com's V3 payload names its tire in the working context only, and
      // a page with no tire context has no page context at all: only the host URL we were sent.
      const sentFrom: PageContext = {
        ...mergedPageContext,
        page_url: mergedPageContext.page_url ?? this._parentPageUrl ?? undefined,
        tire_name: mergedPageContext.tire_name ?? this._workingContext?.page_tire_name ?? undefined,
        page_kind: mergedPageContext.page_kind ?? this._workingContext?.page_type ?? undefined,
      };
      const previousPage = previousPageFor(tabStorage(), STORAGE_KEY_LAST_SENT_PAGE, requestSessionId, sentFrom);
      if (previousPage) {
        body.previousPage = previousPage;
      }
      // TRAC-291 / TRAC-398: ship the backend-canonical working context when set.
      // EmbedPage only sets _workingContext when content handling is on for the current surface.
      // TRAC-396: also ship it (minimally) when the one-shot browse-by-size intent is
      // pending, so the signal reaches the backend even with no page context. Spread into
      // a fresh object so we never persist the one-shot flag on _workingContext.
      if (this._workingContext || this._browseBySizeIntentNextTurn) {
        body.workingContext = {
          ...(this._workingContext ?? {}),
          ...(this._browseBySizeIntentNextTurn ? { browse_by_size_intent: true } : {}),
        };
        this._browseBySizeIntentNextTurn = false; // one-shot: consume after attaching
      }
      if (this._pageData) {
        body.pageData = this._pageData;
      }
      // TRACI-1106: when the server may have lost this conversation (told so, or a long
      // gap since the last reply, longer than the agent keeps a session warm), send this
      // browser's own stored turns. The backend reads them only when its memory for this
      // shopper is empty, so an ordinary message pays nothing for it.
      const storedTurns = this.getMessagesFromSession();
      if (this._carryConversation || idleSinceLastReply(storedTurns)) {
        const prior = buildPriorConversation(storedTurns, typedMessage);
        if (prior.length > 0) body.priorConversation = prior;
      }

      // TRAC-862: this POST must be able to give up.
      //
      // It is awaited BEFORE the SSE listener is registered and before
      // ensureConnection() opens the stream — so it runs entirely outside the
      // idle watchdog's reach. With no timeout, a backend that hangs (e.g. it is
      // itself blocked on a dead agent runtime) hung the whole turn here, and
      // the retry chain sat on "attempt 1" forever because attempt 1 never
      // finished. The circuit breaker protected the STREAMING phase and left the
      // REQUEST phase completely unguarded, which is the shape a tester reported
      // as "the counter stayed at 1".
      const postAbort = new AbortController();
      const postTimer = setTimeout(() => postAbort.abort(), STREAM_IDLE_TIMEOUT_MS);
      let response: Response;
      try {
        response = await fetch(`${getBackendApiUrl()}/agent/user-message`, {
          method: 'POST',
          headers: await this.getRequestHeaders(requestSessionId),
          body: JSON.stringify(body),
          signal: postAbort.signal,
        });
      } catch (err) {
        // Wording is load-bearing: "timeout" routes this to the retry/countdown
        // presentation (TRAC-81) instead of a bare error bubble.
        if ((err as Error)?.name === 'AbortError') {
          throw new Error(
            `Request timeout - the assistant did not respond in ${Math.round(STREAM_IDLE_TIMEOUT_MS / 1000)}s. Please try again.`,
          );
        }
        throw err;
      } finally {
        clearTimeout(postTimer);
      }

      if (!response.ok) {
        // TRAC-1089: the backend no longer lets us act on this session: it predates
        // session ownership and its grace window has closed, or we lost its key. Nothing
        // can recover it, so start a fresh session once rather than dead-end the customer.
        if (response.status === 403 && requestSessionId && !this._ownershipRetry) {
          const errBody = await response.clone().json().catch(() => null);
          if (isSessionNotOwned(response.status, errBody)) {
            logger.warn(`[SESSION_OWNER] Not the owner of ${requestSessionId.substring(0, 12)}... — starting a fresh session`);
            this.forgetUnownedSession(requestSessionId);
            this.carryConversationOnNextMessage(); // TRACI-1106: the fresh session starts with the chat so far
            this._ownershipRetry = true;
            try {
              return await this.sendMessage(message, null, userId, ymmvTimingMs, selectorCompletion, clientTurnId);
            } finally {
              this._ownershipRetry = false;
            }
          }
        }

        // TRAC-276: Handle 401 in embed mode — partner token expired, reload for fresh token
        if (response.status === 401 && this.isEmbedMode()) {
          this.handleExpiredPartnerToken(message);
          // If we reach here, cooldown blocked the reload — fall through to generic error
        }

        // Handle 503 Service Unavailable (emergency shutoff or service down)
        if (response.status === 503) {
          const errorData = await response.json().catch(() => ({}));
          const message = errorData.detail || AGENT_UNAVAILABLE_MESSAGE;
          throw new Error(message);
        }
        // TRACI-472: the backend refused this exact message (too long, malformed).
        // Sending it again cannot work, so say so instead of failing like a dropped connection.
        if (isRejectedStatus(response.status)) {
          const body = await response.clone().json().catch(() => null);
          throw new RequestRejectedError(response.status, rejectedCopy(response.status, body));
        }
        throw new Error(`Backend error: ${response.status} ${response.statusText}`);
      }
      // TRACI-1106: accepted, so the carried conversation reached the new session.
      this._carryConversation = false;

      const data = await response.json();

      // Save the agent session ID for subsequent requests
      if (data.runtimeSessionId) {
        // TRAC-1089: set only when this call created the session; returned only now.
        sessionKeys.remember(data.runtimeSessionId, data.sessionKey);
        this.saveAgentSessionId(data.runtimeSessionId);

      }
      // TRACI-431: only now that the message went through is this the page it was
      // sent from; a failed send leaves the note in place for the retry.
      rememberSentPage(tabStorage(), STORAGE_KEY_LAST_SENT_PAGE, data.runtimeSessionId || requestSessionId, sentFrom);

      return {
        runtimeSessionId: data.runtimeSessionId,
        timestamp: data.timestamp,
        ...(data.skipped ? { skipped: String(data.skipped) } : {}),
        ...(typeof data.answer === 'string' ? { answer: data.answer } : {}),
      };
    } catch (error) {
      logger.error('Error sending message to backend:', error);
      throw error;
    }
  }

  /**
   * TRAC-203: Validate session exists in S3 before sending a message.
   * Uses the lightweight /exists endpoint (MaxKeys=1) instead of /status
   * which paginates all messages. Returns true if valid or indeterminate (fail-open).
   */
  /**
   * TRACI-1106: the server may have lost this conversation (a simulated or auto-cleared
   * session, a fresh session after a 403), so the next message carries this browser's own
   * stored conversation. Cleared once a message is accepted, or on a chat clear.
   */
  private _carryConversation = false;

  /** TRACI-1106: carry the stored conversation on the next message (see _carryConversation). */
  carryConversationOnNextMessage(): void {
    this._carryConversation = true;
  }

  /** TRAC-1089: set while sendMessage retries once on a fresh session after a 403. */
  private _ownershipRetry = false;

  /** TRAC-1089: drop a session this browser can no longer prove it owns. */
  private forgetUnownedSession(sessionId: string): void {
    sessionKeys.forget(sessionId);
    if (this.agentSessionId === sessionId) this.agentSessionId = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_AGENT_SESSION);
      if (raw && JSON.parse(raw).sessionId === sessionId) localStorage.removeItem(STORAGE_KEY_AGENT_SESSION);
    } catch { /* storage unavailable: nothing persisted to drop */ }
  }

  /**
   * TRAC-468 / TRACI-1106: Dev Settings > Simulate Session Expiry. Expires the session the
   * way the server does (its stored memory for this shopper is deleted, the cached agent
   * evicted) and leaves this browser alone, so the next message takes the real path: the
   * chat stays on screen and the new turn carries the earlier conversation.
   */
  async simulateSessionExpiry(): Promise<void> {
    this.carryConversationOnNextMessage();
    try {
      const r = await fetch(`${getBackendApiUrl()}/api/session`, {
        method: 'DELETE',
        headers: await this.getRequestHeaders(),
      });
      logger.info(`[DEV] Simulated session expiry: server memory ${r.ok ? 'deleted' : `not deleted (${r.status})`}`);
    } catch (err) {
      logger.warn('[DEV] Simulated session expiry: delete failed', err);
    }
  }


  /**
   * Ensure SSE connection is established (fresh connection per message).
   * Uses @microsoft/fetch-event-source for Safari compatibility.
   * TRAC-203: Always opens a fresh connection, closing any existing one first.
   */
  async ensureConnection(sessionId: string, lastSequence?: number): Promise<string> {
    if (!sessionId) {
      throw new Error('Session ID is required to establish SSE connection');
    }

    // TRAC-203: Always close any existing connection and open fresh.
    // This eliminates stale connections that silently die after long idle periods
    // (browser/LB idle timeouts) without firing onclose/onerror.
    if (this.abortController) {
      logger.debug('Closing existing SSE connection before opening fresh one');
      this.disconnect();
    }

    logger.debug('Opening fresh SSE connection for session:', sessionId.substring(0, 12), 'embed:', this.isEmbedMode());

    // TRAC-203: Pass lastSequence so the backend only replays events after that point.
    // This prevents previous message events from being replayed into a new message's stream,
    // while still allowing the current message's events through (even if replayed due to race condition).
    const sseUrl = new URL(`${getBackendApiUrl()}/agent/agent-response-stream/${sessionId}`);
    if (lastSequence !== undefined && lastSequence > 0) {
      sseUrl.searchParams.append('lastSequence', lastSequence.toString());
      logger.debug(`[TRAC-203] ensureConnection: passing lastSequence=${lastSequence} to SSE URL`);
    } else {
      logger.debug(`[TRAC-203] ensureConnection: NO lastSequence passed (value=${lastSequence}) — backend will replay ALL events`);
    }
    const url = sseUrl.toString();
    logger.debug(`[TRAC-203] SSE URL: ${url}`);

    // Create new AbortController for this connection
    this.abortController = new AbortController();
    // GR-28: deliberately NOT resetting _sseReconnectAttempts here. An outer
    // auto-retry lands here too, and a fresh budget per call multiplied the cap
    // (see _sseBudgetTurnMessage); sendMessageAndStream resets it per new turn.
    this._sseConnectionOpenedAt = null;

    let currentMessageId: string | null = null;
    // TRAC-1089: a 403 means this session is not ours to read. Retrying cannot change
    // that, and an endless retry is the reconnect loop that trips the firewall (TRAC-1081).
    let refused = false;

    // Use fetchEventSource for Safari compatibility
    // This runs in the background and distributes events to listeners
    fetchEventSource(url, {
      method: 'GET',
      headers: {
        ...await this.getStreamHeaders(sessionId),
        'Accept': EventStreamContentType,
      },
      signal: this.abortController.signal,

      onopen: async (response) => {
        if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
          logger.debug('SSE connection opened successfully');
          this._setReconnecting(false);
          // GR-28: don't zero the counter here — a connection that opens and then
          // errors again right away (adversarial review: an LB or backend that
          // flaps instead of failing to open) would reset every cycle and never
          // hit the cap. Only record when it opened; onerror decides whether it
          // stayed up long enough to earn a fresh budget.
          this._sseConnectionOpenedAt = Date.now();
          return; // Everything is good
        } else if (response.status === 403) {
          refused = true;
          throw new Error('SSE connection refused: 403 (session not owned)');
        } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          // Client-side errors (except rate limiting) are not retriable
          throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
        } else {
          // Server errors or rate limiting - will be retried
          throw new Error(`SSE connection error: ${response.status} ${response.statusText}`);
        }
      },

      onmessage: (ev) => {
        // Parse the SSE event
        const event = parseSSEEvent(`data: ${ev.data}`);
        if (!event) return;

        // Debug: Log all tool events
        if (event.type?.startsWith('tool_use')) {
          logger.debug(`[SSE] Tool event received: ${event.type}`, {
            toolName: event.data?.toolName,
            toolId: event.data?.toolId,
            sequence: event.sequence
          });
        }

        // Track current message ID from response_start events
        if (event.type === 'response_start' && event.data?.messageId) {
          currentMessageId = event.data.messageId as string;
        }

        // Distribute event to all listeners (each message handler)
        for (const [listenerId, callback] of this.eventListeners.entries()) {
          try {
            callback(event, currentMessageId);
          } catch (e) {
            logger.error(`Error in listener ${listenerId}:`, e);
          }
        }
      },

      onclose: () => {
        // A clean close ends fetchEventSource's loop for good (2.0.x resolves
        // after onclose; it never reopens on its own), so it cannot spin past
        // the reconnect cap. If this ever throws to force a reconnect, the
        // throw lands in onerror below and spends the same budget and backoff.
        // Pinned by RealChatAgentService.cleanClose.test.ts (real library).
        logger.debug('SSE connection closed by server');
      },

      onerror: (err) => {
        logger.error('SSE connection error:', err);
        // GR-28: a connection that proved itself (stayed open at least
        // SSE_STABLE_CONNECTION_MS) earns a fresh budget on its next failure —
        // that's a new problem, not a continuation of the last one. A connection
        // that never opened, or opened and errored again almost immediately,
        // keeps counting against the same budget so a flap loop still hits the cap.
        // A long quiet spell since the last error (e.g. the shopper's next
        // message minutes later) also starts a new budget.
        const now = Date.now();
        const wasStable =
          this._sseConnectionOpenedAt !== null &&
          now - this._sseConnectionOpenedAt >= RealChatAgentService.SSE_STABLE_CONNECTION_MS;
        const quietSinceLastError =
          this._sseLastErrorAt !== null &&
          now - this._sseLastErrorAt >= RealChatAgentService.SSE_BUDGET_REFILL_MS;
        this._sseConnectionOpenedAt = null;
        this._sseLastErrorAt = now;
        if (wasStable || quietSinceLastError) {
          this._sseReconnectAttempts = 0;
        }
        this._sseReconnectAttempts += 1;
        // GR-28: stop once we're aborted, refused (TRAC-1089: not our session), or
        // once we're out of retry budget — uncapped retries turned one bad stream
        // into a request storm (see the field comment on SSE_MAX_RECONNECT_ATTEMPTS
        // above).
        const giveUp =
          this.abortController?.signal.aborted ||
          refused ||
          this._sseReconnectAttempts > RealChatAgentService.SSE_MAX_RECONNECT_ATTEMPTS;
        if (!giveUp) {
          // TRAC-300: Fire-and-forget error report. Gated on giveUp so a stuck
          // stream can't turn into an unbounded stream of these POSTs either,
          // and throttled per type (reportClientError) on top of that.
          reportClientError('sse_connection_error');
        }
        if (giveUp) {
          throw err; // Stop retrying — aborted, refused, or out of attempts (GR-28)
        }
        // GR-28: exponential backoff (1s, 2s, 4s, 8s, 16s, 20s cap) instead of
        // fetch-event-source's fixed ~1s default interval. Returning a number
        // from onerror sets the delay before the next retry.
        return Math.min(
          RealChatAgentService.SSE_RECONNECT_BASE_MS * 2 ** (this._sseReconnectAttempts - 1),
          RealChatAgentService.SSE_RECONNECT_MAX_MS,
        );
      },

      // Keep connection open and retry on errors, up to the cap above
      openWhenHidden: true,
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        logger.error('SSE fetchEventSource error:', error);
      }
    });

    return sessionId;
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(): void {
    this.closeActiveConnection();
  }

  /**
   * Send message and stream response (uses shared SSE connection)
   */
  async *sendMessageAndStream(
    message: string,
    userId: string | null = null,
    ymmvTimingMs?: { elapsedMs: number; openedAt: number } | null,
    selectorCompletion?: SelectorCompletion | null,
  ): AsyncGenerator<StreamChunk> {
    const listenerId = `listener_${Date.now()}_${Math.random()}`;
    const eventQueue: SSEEvent[] = [];
    let resolveNext: (() => void) | null = null;
    let expectedMessageId: string | null = null;
    let isComplete = false;
    /** TRACI-864: the turn ended on the idle watchdog, so its resend reuses the clientTurnId. */
    let watchdogFired = false;
    /** TRAC-862: stops an injected fault script when the turn ends. */
    let stopInjectedFault: (() => void) | null = null;

    try {
      // TRAC-468: Close any existing SSE connection (e.g., from a resume-interrupted-stream
      // flow after page refresh) and clear stale listeners before sending a new message.
      // Without this, the resume connection's events can leak into the new message's listener.
      this.closeActiveConnection();
      this.eventListeners.clear();

      // GR-28: a new message gets a fresh SSE reconnect budget; an auto-retry of
      // the failing turn (same text) keeps spending the one it already has.
      if (message !== this._sseBudgetTurnMessage) {
        this._sseBudgetTurnMessage = message;
        this._sseReconnectAttempts = 0;
      }

      // TRAC-862: dev-only fault injection. When armed, this turn never reaches
      // the backend — a script feeds the SAME listener the real transport uses,
      // so the reader loop, idle watchdog, heartbeat handling, retry scheduler
      // and overlay all run for real against a chosen failure shape.
      const injectedFault = isProdBuild() ? null : consumeFault(); // TRACI-933: never on prod

      // Step 1: Send message to get/create session ID
      let runtimeSessionId: string;
      if (injectedFault) {
        runtimeSessionId = this.agentSessionId || `fault-session-${Date.now()}`;
      } else {
        // TRACI-864: same id for a resend of the message still waiting on its reply.
        // Keyed on the text and the selector pick, so a different pick is a new turn.
        const turnKey = `${message}\u0000${JSON.stringify(selectorCompletion ?? null)}`;
        const clientTurnId = this._unansweredTurn?.key === turnKey
          ? this._unansweredTurn.clientTurnId
          : newClientTurnId();
        this._unansweredTurn = { key: turnKey, clientTurnId };
        const sent = await this.sendMessage(message, this.agentSessionId, userId, ymmvTimingMs, selectorCompletion, clientTurnId);
        runtimeSessionId = sent.runtimeSessionId;
        // TRACI-864: the server already ran this message's turn. Show the reply it stored
        // (the stream that carried it never reached us) instead of running it again.
        if (sent.skipped === 'already_answered') {
          logger.info('[TRACI-864] resent message already answered; showing the stored reply');
          this._unansweredTurn = null;
          if (sent.answer) yield { type: 'content', content: sent.answer };
          yield { type: 'done' };
          return;
        }
        // Still running on the server: report the same timeout, so the retry scheduler
        // checks again at its next backoff rather than starting a second turn.
        if (sent.skipped === 'turn_in_progress') {
          logger.info('[TRACI-864] resent message still being answered; checking again later');
          yield { type: 'error', error: 'Stream timeout - no response received. Please try again.' };
          return;
        }
        // TRACI-863: nothing will stream for this message (another tab already welcomed the
        // customer back, or a rep still has the chat), so don't open a stream and wait on it.
        if (sent.skipped) {
          logger.info(`[TRACI-863] backend skipped this turn: ${sent.skipped}`);
          yield { type: 'turn_skipped', status: sent.skipped };
          return;
        }
      }
      logger.debug(`Using session ID: ${runtimeSessionId}`);

      // TRAC-468: Register event listener BEFORE opening SSE connection.
      // fetchEventSource dispatches events asynchronously — if the listener isn't
      // registered before the connection opens, early events (response_start, first
      // chunks) are silently dropped into an empty eventListeners map. This race
      // condition caused "Spinning up results..." to hang after page refresh.
      this.eventListeners.set(listenerId, (event: SSEEvent, currentMessageId: string | null) => {
        // TRAC-203: Log every incoming event for debugging replay issues
        const isReplayed = (event as Record<string, unknown>).replayed;
        logger.debug(`[TRAC-203] Event received: type=${event.type} seq=${event.sequence} replayed=${isReplayed} msgId=${(event.data?.messageId as string)?.substring(0, 8) || 'n/a'} currentMsgId=${currentMessageId?.substring(0, 8) || 'n/a'} expectedMsgId=${expectedMessageId?.substring(0, 8) || 'n/a'}`);

        // Track our message ID from response_start
        if (event.type === 'response_start' && event.data?.messageId && !expectedMessageId) {
          expectedMessageId = event.data.messageId as string;
          logger.debug(`[TRAC-203] ✅ Captured our messageId: ${expectedMessageId}`);
        }

        // TRAC-256: Persist sequence numbers as events arrive so lastSequence stays current.
        // Without this, lastSequence only updates during the resume flow (streamResponse),
        // causing it to go stale. A stale lastSequence can cause the DynamoDB replay to
        // skip new events when the sequence counter resets after METADATA TTL expiration.
        if (event.sequence !== undefined && event.sequence > 0) {
          this.persistLastSequence(event.sequence);
        }

        // Event filtering: Prevent old events from leaking into new messages.
        // TRAC-203: Use expectedMessageId when available for strict filtering.
        // TRAC-256: When expectedMessageId is not set (response_start missed due to
        // race condition), accept fresh (non-replayed) events from the current connection.
        // This handles the case where response_start is broadcast before the SSE queue
        // exists and not replayed due to DynamoDB sequence counter reset after TTL expiry.
        if (!expectedMessageId) {
          if (event.type === 'response_start') {
            // Always accept response_start — this is how we capture expectedMessageId
          } else if (isReplayed) {
            // Reject replayed events when we haven't identified our message yet —
            // these are likely from a previous message's DynamoDB buffer
            logger.debug(`[TRAC-256] ⏭️ Skipping replayed ${event.type} (no response_start yet, could be old message)`);
            return;
          } else {
            // TRAC-256: Accept fresh (non-replayed) events even without response_start.
            // Since TRAC-203 uses a fresh connection per message, non-replayed events
            // on this connection belong to the current message.
            logger.debug(`[TRAC-256] ✅ Accepting fresh ${event.type} without response_start (seq=${event.sequence})`);
          }
        } else if (currentMessageId && currentMessageId !== expectedMessageId) {
          // We know our messageId, reject events from other messages
          logger.debug(`[TRAC-203] ⏭️ Skipping event from different message: ${currentMessageId?.substring(0, 8)} !== ${expectedMessageId?.substring(0, 8)}`);
          return;
        }

        // TRAC-232: Check messageId on completion events to prevent a previous message's
        // delayed response_complete from prematurely terminating the current stream.
        // This happens when Message N's response_complete is broadcast after Message N+1's
        // response_start (due to conversation logging delay between the two broadcasts).
        const eventMessageId = event.data?.messageId as string | undefined;
        if (expectedMessageId && eventMessageId && eventMessageId !== expectedMessageId) {
          logger.debug(`[TRAC-232] ⏭️ Skipping event with stale messageId: ${eventMessageId.substring(0, 8)} !== ${expectedMessageId.substring(0, 8)}`);
          return;
        }

        // TRAC-862: connection-level keepalives are not agent liveness. Drop them
        // HERE rather than in conversion — conversion runs after the event has
        // already woken the reader and reset the idle timer, which is precisely
        // how a dead agent kept the watchdog alive indefinitely.
        if (CONNECTION_KEEPALIVE_EVENTS.has(event.type)) {
          return;
        }

        // Queue the event
        eventQueue.push(event);

        // Wake up the generator if it's waiting
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }

        // Check if this is a completion event
        if (event.type === 'response_complete' || event.type === 'response_error') {
          isComplete = true;
        }
        // GR-28: a whole answer made it through, so the stream works; refill the
        // shared reconnect budget. response_error doesn't count: an agent that
        // errors fast behind a flapping stream would otherwise refill it per retry.
        if (event.type === 'response_complete') {
          this._sseReconnectAttempts = 0;
          this._unansweredTurn = null; // TRACI-864: the reply arrived; the next send is a new turn
        }
      });

      // Step 3: NOW open the SSE connection (listener is already registered above)
      if (injectedFault) {
        // Feed the script straight to the registered listener instead of opening
        // a real connection. Cleanup is registered on the generator's finally
        // below — an unstopped script (heartbeat-only especially) would keep
        // emitting into a listener that has moved on to the next turn.
        stopInjectedFault = playFault(injectedFault, (event) => {
          const listener = this.eventListeners.get(listenerId);
          if (listener) listener(event as unknown as SSEEvent, null);
        });
      } else {
        await this.ensureConnection(runtimeSessionId);
      }

      // Stream events from the queue
      while (!isComplete || eventQueue.length > 0) {
        // Wait for events if queue is empty
        if (eventQueue.length === 0) {
          const timedOut = await new Promise<boolean>(resolve => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const wrappedResolve = (didTimeOut: boolean) => {
              if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              resolve(didTimeOut);
            };
            resolveNext = () => wrappedResolve(false);
            // No-activity timeout (TRAC-81: 30s→90s; TRAC-862: 90s→45s, measured)
            timeoutId = setTimeout(() => {
              // TRAC-300: Report SSE timeout
              reportClientError('sse_timeout');
              wrappedResolve(true);
            }, STREAM_IDLE_TIMEOUT_MS);
          });

          // TRAC-850: CIRCUIT BREAKER. This timeout used to only report a metric and
          // then loop back into another 90s wait — forever. `isComplete` flips only on
          // a response_complete/response_error event, so an open-but-silent stream
          // (backend hung, agent died mid-turn, event dropped) left this generator
          // spinning, its consumer's `for await` never exited, its `finally` never ran,
          // and the composer stayed locked with a spinner until the user cleared the
          // conversation. Wording matters: the consumer routes on
          // error.includes('timeout') (TRAC-81) to show the countdown + retry
          // affordance rather than a generic failure.
          if (timedOut && eventQueue.length === 0) {
            isComplete = true;
            watchdogFired = true;
            this.disconnect();
            yield {
              type: 'error',
              error: 'Stream timeout - no response received. Please try again.',
            };
            break;
          }
        }

        // Process all queued events
        while (eventQueue.length > 0) {
          const event = eventQueue.shift()!;
          const chunk = this.convertBackendEventToFrontend(event);
          if (chunk) {
            if (chunk.type === 'done') this._unansweredTurn = null; // TRACI-864
            yield chunk;
          }
        }
      }

      // TRACI-864: the stream finished on its own, so the turn is over (answered or
      // errored); only a watchdog timeout leaves the id for the resend to reuse.
      if (!watchdogFired) this._unansweredTurn = null;

      // TRAC-203: Close SSE connection after each response completes.
      // This prevents stale connections that silently die after long idle periods.
      // The next message will open a fresh connection via ensureConnection().
      this.disconnect();

    } catch (error) {
      logger.error('Error in sendMessageAndStream:', error);
      this.disconnect();
      yield {
        type: 'error',
        error: (error as Error).message,
        // TRACI-472: a refused request is final; the chat shows the copy and does not retry.
        ...(error instanceof RequestRejectedError
          ? { data: { requestRejected: true, userMessage: error.userMessage } }
          : {}),
      };
    } finally {
      // TRAC-862: stop any injected fault script first — heartbeat-only and
      // slow-ok run on intervals and would otherwise outlive the turn.
      stopInjectedFault?.();
      // Remove this listener
      this.eventListeners.delete(listenerId);
    }
  }

  /**
   * Stream response from agent (Step 2: SSE streaming)
   * Uses @microsoft/fetch-event-source for Safari compatibility.
   */
  async *streamResponse(sessionId: string, lastSequence: number | null = null): AsyncGenerator<StreamChunk> {
    // Close any existing connection
    this.closeActiveConnection();

    // Build URL with query parameters
    const url = new URL(`${getBackendApiUrl()}/agent/agent-response-stream/${sessionId}`);
    // TRAC-468: Pass lastSequence when provided (including 0 for replay-from-start).
    // null = don't append param → backend skips replay (for new messages via ensureConnection).
    if (lastSequence !== null && lastSequence >= 0) {
      url.searchParams.append('lastSequence', lastSequence.toString());
    }

    // Create abort controller for this stream
    const abortController = new AbortController();
    this.abortController = abortController;

    // Queue for events from the callback-based fetchEventSource
    const eventQueue: StreamChunk[] = [];
    let resolveNext: (() => void) | null = null;
    let isComplete = false;
    let streamError: Error | null = null;
    let lastReceivedSequence = lastSequence || 0;
    // TRAC-862: did this stream deliver anything the customer would count as a
    // response? Decides whether an abnormal close is end-of-stream or a failure
    // (see onclose). Keepalives and connection chatter deliberately don't count —
    // they prove the socket worked, not that the turn did.
    let receivedAnyContent = false;

    // Get headers before starting the stream
    const headers = await this.getStreamHeaders(sessionId);

    // Start fetchEventSource (runs in background)
    fetchEventSource(url.toString(), {
      method: 'GET',
      headers: {
        ...headers,
        'Accept': EventStreamContentType,
      },
      signal: abortController.signal,

      onopen: async (response) => {
        if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
          logger.debug('SSE stream opened successfully');
          this._setReconnecting(false);
          return;
        } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new Error(`Stream error: ${response.status} ${response.statusText}`);
        } else {
          throw new Error(`Stream error: ${response.status} ${response.statusText}`);
        }
      },

      onmessage: (ev) => {
        const event = parseSSEEvent(`data: ${ev.data}`);
        if (!event) return;

        // Update sequence number if present
        if (event.sequence !== undefined) {
          lastReceivedSequence = event.sequence;
          this.updateStreamState(null, lastReceivedSequence, true);
        }

        // Convert and queue the event
        // Pass skipReplayedContent=false because streamResponse is used for resume flow
        // where we WANT replayed events to rebuild the interrupted message (TRAC-203)
        // The sequence number filtering (lastSequence param) handles which events we receive
        // TRAC-862: an agent keepalive is activity but not content. This loop
        // queues *converted* chunks and only wakes the reader when conversion
        // returns something, so a null-converting keepalive would be invisible
        // here — wake the reader explicitly and drop the frame.
        if (event.type === AGENT_KEEPALIVE_EVENT) {
          if (resolveNext) {
            resolveNext();
            resolveNext = null;
          }
          return;
        }

        const chunk = this.convertBackendEventToFrontend(event, false);
        if (chunk) {
          // TRAC-862: anything the customer would see counts as a real response,
          // so a later close is end-of-stream rather than a dropped turn. An
          // `error` chunk explicitly does not count.
          if (chunk.type !== 'error') {
            receivedAnyContent = true;
          }
          eventQueue.push(chunk);

          // Check for completion
          if (chunk.type === 'done' || chunk.type === 'error') {
            isComplete = true;
            this.updateStreamState(null, lastReceivedSequence, false);
          }

          // Wake up the generator
          if (resolveNext) {
            resolveNext();
            resolveNext = null;
          }
        }
      },

      onclose: () => {
        logger.debug('SSE stream closed');
        if (!isComplete) {
          // TRAC-862: an abnormal close is a FAILURE, not a success.
          //
          // This used to push `done` unconditionally. Downstream, `done` means
          // "the turn succeeded": useChatStream resets retryAttempt, clears the
          // counter and calls scheduler.abandon(). So when the server dropped a
          // stream mid-turn, every retry's failure was laundered into a success,
          // the chain restarted from attempt 0, and the banner sat on
          // "attempt 1 of N" forever while silently looping. Exhaustion could
          // never be reached, which is also why the emergency overlay never
          // engaged in practice — `onExhausted` was unreachable from here.
          //
          // A close AFTER content is a normal end-of-stream and still means done
          // (the resume/replay flow in TRAC-203/468 depends on that). A close
          // with nothing received is the abnormal case, and it has to enter the
          // retry chain. "timeout" wording routes it to the retry presentation.
          if (receivedAnyContent) {
            eventQueue.push({ type: 'done' });
          } else {
            eventQueue.push({
              type: 'error',
              error: 'Stream timeout - the connection closed before any response arrived. Please try again.',
            });
          }
          isComplete = true;
          this.updateStreamState(null, lastReceivedSequence, false);
        }
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }
      },

      onerror: (err) => {
        logger.error('SSE stream error:', err);
        // TRAC-300: Fire-and-forget error report
        reportClientError('sse_stream_error');
        if (abortController.signal.aborted) {
          throw err; // Stop retrying if we aborted
        }
        streamError = err instanceof Error ? err : new Error(String(err));
        isComplete = true;
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }
        throw err; // Don't retry on error for this use case
      },
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        logger.error('SSE fetchEventSource error:', error);
        streamError = error instanceof Error ? error : new Error(String(error));
        isComplete = true;
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }
      }
    });

    try {
      // Yield events from the queue
      while (!isComplete || eventQueue.length > 0) {
        // Wait for events if queue is empty
        if (eventQueue.length === 0 && !isComplete) {
          const timedOut = await new Promise<boolean>(resolve => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const wrappedResolve = (didTimeOut: boolean) => {
              if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              resolve(didTimeOut);
            };
            resolveNext = () => wrappedResolve(false);
            // No-activity timeout (TRAC-81: 30s→90s; TRAC-862: 90s→45s, measured)
            timeoutId = setTimeout(() => {
              // TRAC-300: Report SSE timeout
              reportClientError('sse_timeout');
              wrappedResolve(true);
            }, STREAM_IDLE_TIMEOUT_MS);
          });

          // TRAC-850: CIRCUIT BREAKER — see the sibling loop above. Without this, a
          // silent stream spins here indefinitely and the composer never unlocks.
          if (timedOut && eventQueue.length === 0) {
            isComplete = true;
            yield {
              type: 'error',
              error: 'Stream timeout - no response received. Please try again.',
            };
            break;
          }
        }

        // Process all queued events
        while (eventQueue.length > 0) {
          yield eventQueue.shift()!;
        }

        // Check for errors
        if (streamError) {
          yield {
            type: 'error',
            error: streamError.message
          };
          break;
        }
      }
    } finally {
      // Clean up
      if (this.abortController === abortController) {
        this.abortController = null;
      }
    }
  }

  // ─── TRAC-732: LivePerson live-agent handoff ──────────────────────────────

  /** TRAC-732 intent model: the customer's connect/leave actions are numbered so the LATEST one
   *  always wins regardless of the order requests reach the server. The number lives in
   *  localStorage so it survives a reload. Reading it: {seq, wantsHuman}. */
  private _readIntent(): { seq: number; wantsHuman: boolean } {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_HANDOFF_INTENT);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.seq === 'number') return { seq: p.seq, wantsHuman: !!p.wantsHuman };
      }
    } catch { /* corrupt/quota — start fresh */ }
    return { seq: 0, wantsHuman: false };
  }
  /** Bump the intent number for a new action and persist it. Returns the new number. */
  private _bumpIntent(wantsHuman: boolean): number {
    const next = this._readIntent().seq + 1;
    try { localStorage.setItem(STORAGE_KEY_HANDOFF_INTENT, JSON.stringify({ seq: next, wantsHuman })); } catch { /* quota */ }
    return next;
  }

  /** Escalate the current session to a live human agent. */
  /** Start minting the LivePerson monitoring session as soon as the customer heads into a
   *  handoff, so its ids are ready by the time we call escalate (see lpMonitor.ts). */
  prewarmLpSession(): void {
    void ensureLpSession();
  }

  async escalateToHuman(
    transcript: Array<{ role: string; text: string; name?: string; live?: boolean }>,
    customer?: { name?: string; vehicle?: string; customerId?: string } | null,
    department?: string,
    // TRAC-1066: called synchronously on the line before the request leaves. Everything above
    // that line can wait (the LivePerson session alone waits up to 8 s, TRACI-629), so a caller that needs
    // to know "did the request leave this browser" can only learn it from here.
    onSending?: () => void,
    // TRAC-1212: who started this handoff, for the server's handoff_accepted / _declined
    // records. Measurement only; the backend drops anything it does not recognise.
    initiatedBy?: HandoffInitiator,
  ): Promise<{ status: string; department: string | null; reason: string | null; hours: string | null; provider: 'connect' | 'liveperson' | null; testRoute: boolean }> {
    // TRAC-732: accept a *pending* session (loadAnySessionId) — a handoff is
    // valid before any message is sent. loadAgentSessionId() returns null for
    // pending sessions, which made escalate send runtimeSessionId:null → 422.
    //
    // TRAC-1032: and when there is no session at all, start one. A customer who opens
    // the chat and asks for a person before typing has none yet: inside the page shell
    // the chat mints nothing of its own, and when the page never seeds one there is
    // nothing to ask under. The first message copes because the backend mints for a
    // message with no id; the ask does not (a 400). So do here what the first message
    // does — start the session, then ask under it — and never post the ask without an
    // id: that request can only fail, and it was being shown as the desk refusing.
    // A mint that fails throws tagged as the session stage (see requestSession).
    const sessionId = this.agentSessionId || this.loadAnySessionId() || await this.requestSession('handoff');
    // TRAC-732 intent model: stamp this connect with the next action number so the backend can
    // reject it if a newer action (e.g. a leave the customer tapped right after) has since landed.
    const intentSeq = this._bumpIntent(true);
    // TRAC-732: carry the vehicle / size / page the customer was working under into
    // the handoff, even when they never picked a vehicle IN chat. `page_vehicle` comes
    // from the TR.com page itself (buildAIAssistData's headerVehicle); `chat_vehicle`
    // from an in-chat YMMV pick. We keep them DISTINCT on purpose — the rep should see
    // BOTH when they diverge (looking at one car, discussing another). page_url is the
    // parent page's URL — the parent sends it as `data.url` in the buildAIAssistData
    // payload, and EmbedPage stows it on `_pageContext.page_url` (NOT WorkingContext,
    // which has no url; and NOT _pageData, which is tireDetailData only).
    const wc = this._workingContext;
    const fmtVeh = (v?: { year: string; make: string; model: string; modelClarifier?: string | null } | null) =>
      v ? ([v.year, v.make, v.model, v.modelClarifier].filter(Boolean).join(' ').trim() || null) : null;
    // TRAC-945: the same vehicle WITHOUT the trim clarifier, for the line the rep pastes
    // to the customer. The full string is right for the rep's context block, but nobody
    // writes "your 2006 BMW 530i Sedan Base Model With Run-Flat Tires" to a customer —
    // they write "your 2006 BMW 530i". Carried as its own field because the joined string
    // can't be split back reliably (a model can contain spaces: Land Rover Range Rover).
    const fmtVehShort = (v?: { year: string; make: string; model: string } | null) =>
      v ? ([v.year, v.make, v.model].filter(Boolean).join(' ').trim() || null) : null;
    const fmtSize = (s?: { front: string; rear: string } | null) =>
      s?.front ? (s.rear && s.rear !== s.front ? `${s.front} front / ${s.rear} rear` : s.front) : null;
    const handoffContext = {
      chatVehicle: fmtVeh(wc?.chat_vehicle),
      pageVehicle: fmtVeh(wc?.page_vehicle),
      chatVehicleShort: fmtVehShort(wc?.chat_vehicle),
      pageVehicleShort: fmtVehShort(wc?.page_vehicle),
      size: fmtSize(wc?.chat_size ?? wc?.page_selected_size ?? wc?.page_searched_size),
      pageUrl: this._pageContext?.page_url ?? this._pageData?.page_url ?? null,
      // TRAC-928: the loader's page family, so the server-side funnel events carry the
      // page too and the tab does not depend on the browser-side recorder for it.
      pageKind: this._pageContext?.page_kind ?? null,
    } as Record<string, unknown>;
    // The LivePerson visitor monitoring session (minted in-embed, monitor-only). Passing its
    // ids lets the rep get the hyperlink button + device/source context. TRACI-629: this is
    // the one place every handoff passes (button, picker, the agent's own "Connecting you"),
    // so it loads the tag, waits up to 8 s for the session, pushes the page's cart into it,
    // and only then lets the conversation be created. Best-effort: not ready in time means
    // the handoff proceeds without it, as before.
    let minted: { visitorId: string; sessionId: string } | null = null;
    try {
      minted = await prepareLpHandoff(8000);
      if (minted) { handoffContext.lpVisitorId = minted.visitorId; handoffContext.lpSessionId = minted.sessionId; }
    } catch { /* handoff proceeds without the LP session */ }
    // CPOC-111 / CPOC-126: the page's OWN LivePerson session: loader.js's report when the page runs
    // it, else the site's LPVID / LPSID cookies (shared across .tirerack.com) as they were before
    // our tag loaded. The server reads it first and falls back to the minted one, so an embed-only
    // session no longer costs the rep the shopper's visit.
    const cookieLp = lpCookieSessionForHandoff();
    handoffContext.lpCookieMatch = lpCookieMatch(cookieLp, minted);
    const siteLp = siteLpForHandoff() ?? cookieLp;
    if (siteLp) {
      handoffContext.lpSiteVisitorId = siteLp.visitorId;
      handoffContext.lpSiteSessionId = siteLp.sessionId;
      handoffContext.lpSiteAccount = siteLp.site;
    }
    // CPOC-39 / TRACI-1011: the cart and pages this tab saw, for the rep (the Connect POC desk
    // or the LivePerson handoff message).
    const journey = handoffJourney(import.meta.env.VITE_ENV || 'dev');
    if (journey) {
      if (journey.cart) handoffContext.cart = journey.cart;
      if (journey.pages.length) handoffContext.pages = journey.pages;
    }
    // TRAC-732: time the connect and TAG any failure so the caller can log WHY it broke.
    // A cross-origin/third-party iframe (the extension embed) can have this fetch blocked or
    // aborted even though the backend succeeds — that reason only survives if we capture it.
    const _escStart = Date.now();
    const postEscalate = async (sid: string, first: boolean): Promise<Response> => {
      try {
        const headers = await this.getRequestHeaders(sid);
        if (first) onSending?.();
        return await fetch(`${getBackendApiUrl()}/agent/escalate-to-human`, {
          method: 'POST',
          headers,
          // department: the agent's pick (Sales/Customer Care) when it called
          // route_to_human; null for a manual click → backend classifies.
          body: JSON.stringify({ runtimeSessionId: sid, transcript, customer: customer ?? null, department: department ?? null, intentSeq, handoffContext, initiatedBy: initiatedBy ?? null, ...(this._opening ? { opening: this._opening } : {}) }),
        });
      } catch (netErr) {
        // Network-level rejection: CORS block, connection reset, aborted (iframe torn down), offline.
        const e = new Error(`Escalation network error: ${(netErr as Error)?.message ?? netErr}`);
        Object.assign(e, { stage: 'escalate', httpStatus: 0, netError: true, elapsedMs: Date.now() - _escStart });
        throw e;
      }
    };
    let res = await postEscalate(sessionId, true);
    // TRACI-1093: a browser can hold a session for months, but its owner row is reaped
    // after 30 days (OWNER_ROW_TTL_DAYS), so the backend refuses it with session_not_owned
    // and nothing can recover that session. Do what sendMessage does: drop it, start a
    // fresh one, and ask again once. The transcript rides in the body, so the rep still
    // gets the conversation. A second refusal fails as before.
    if (res.status === 403) {
      const errBody = await res.clone().json().catch(() => null);
      if (isSessionNotOwned(res.status, errBody)) {
        logger.warn(`[SESSION_OWNER] Not the owner of ${sessionId.substring(0, 12)}... at escalate; retrying on a fresh session`);
        this.forgetUnownedSession(sessionId);
        let freshId: string;
        try {
          freshId = await this.requestSession('handoff');
        } catch (mintErr) {
          // Report the 403 that sent us here, not the mint: the caller already tells the
          // customer "couldn't connect" for either, and the 403 is the real cause.
          logger.warn('[SESSION_OWNER] Fresh session for escalate failed:', mintErr);
          freshId = '';
        }
        if (freshId) res = await postEscalate(freshId, false);
      }
    }
    if (!res.ok) {
      const e = new Error(`Escalation failed: ${res.status}`);
      Object.assign(e, { stage: 'escalate', httpStatus: res.status, elapsedMs: Date.now() - _escStart });
      throw e;
    }
    // The backend echoes: status ("escalating" | "no_agents_available"), the
    // resolved department (for naming the team in the "Connecting…" turn), and —
    // when nobody's available — a reason ("no_agents" | "closed_hours") + hours.
    try {
      const body = await res.json();
      return {
        status: (body?.status as string) || 'escalating',
        department: (body?.department as string) || null,
        reason: (body?.reason as string) ?? null,
        hours: (body?.hours as string) ?? null,
        // TRACI-1053: which desk took the chat; only the two known values, never raw input.
        provider: body?.provider === 'connect' || body?.provider === 'liveperson' ? body.provider : null,
        // CPOC-109: the backend's own call that a tester or staff route sent this chat to
        // Connect (never a share-dial shopper). Only a literal true counts; absent is a shopper.
        testRoute: body?.testRoute === true,
      };
    } catch {
      return { status: 'escalating', department: null, reason: null, hours: null, provider: null, testRoute: false };
    }
  }

  /**
   * TRAC-732: forward a browser-side LivePerson FAILURE to the backend so it lands in
   * Datadog (searchable by [LP_FAIL]). Fire-and-forget: never throws, never blocks the UI.
   * Reuses the standard auth headers. Callers (via utils/clientLog) must send only
   * metadata — reasons, statuses, timings, browser-context booleans — never message text.
   */
  async postClientLog(body: { event: string; reason: string | null; detail: Record<string, unknown>; sessionId: string | null; level: string }): Promise<void> {
    try {
      await fetch(`${getBackendApiUrl()}/agent/client-log`, {
        method: 'POST',
        headers: await this.getRequestHeaders(),
        body: JSON.stringify(body),
        keepalive: true,   // survive an iframe/page teardown so the reason still reaches us
      });
    } catch {
      /* telemetry is best-effort — swallow */
    }
  }

  /**
   * TRAC-732: Store the customer's end-of-conversation feedback (CSAT +
   * resolution + free text + transcript) in TRACI's own system of record.
   *
   * Called each time the customer answers a survey step (progressive: CSAT ->
   * resolved -> free text), so partial submissions are captured — a lone CSAT is
   * a valid record. Upserts one item per session; latest answers win. The score
   * + resolution are separately mirrored into LivePerson's CSAT reporting by the
   * backend; the free text lives only here (LP can't ingest it via our channel).
   *
   * Fire-and-forget from the caller's perspective — returns false on any error
   * rather than throwing, so a storage hiccup never blocks the survey UI.
   */
  async submitConversationFeedback(input: {
    csat: number;
    resolved?: boolean | null;
    feedback?: string | null;
    department?: string | null;
    transcript?: Array<{ role: string; text: string; name?: string; timestamp?: string }>;
    lpConversationId?: string | null;
    userEmail?: string | null;
  }): Promise<boolean> {
    try {
      const sessionId = this.agentSessionId || this.loadAnySessionId();
      if (!sessionId) return false;
      // CPOC-140: the comment and the transcript are redacted before they leave the browser.
      const feedbackText = input.feedback ? safeRedact(input.feedback).text : (input.feedback ?? null);
      const turns = input.transcript
        ? input.transcript.map((t) => ({ ...t, text: safeRedact(t.text).text }))
        : null;
      if (turns) {
        const shopper = turns.filter((t) => t.role === 'user' || t.role === 'customer');
        splitCardIndices(shopper.map((t) => t.text)).forEach((i) => { shopper[i].text = CARD_TOKEN; });
      }
      const res = await fetch(`${getBackendApiUrl()}/api/conversation-feedback`, {
        method: 'POST',
        headers: await this.getRequestHeaders(sessionId),
        body: JSON.stringify({
          sessionId,
          csat: input.csat,
          resolved: input.resolved ?? null,
          feedback: feedbackText,
          department: input.department ?? null,
          transcript: turns,
          lpConversationId: input.lpConversationId ?? null,
          userEmail: input.userEmail ?? null,
          timestamp: new Date().toISOString(),
        }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** TRAC-732: gate for the "Live Chat Available" button — working hours + live
   *  agent availability. Fails OPEN on any error so a backend hiccup never hides
   *  the button. state: "open" | "closed_hours" | "closed_no_agents".
   *  TRAC-965: a fail-open answer is marked `failOpen` so measurement (Adobe's
   *  "live chat available") can tell a desk LivePerson said was open from a
   *  swallowed error. The button keeps treating both as open. */
  async getLiveChatAvailability(): Promise<LiveChatAvailability> {
    try {
      const res = await fetch(`${getBackendApiUrl()}/agent/live-chat-availability`, {
        headers: await this.getRequestHeaders(),
      });
      if (!res.ok) return toLiveChatAvailability(null);
      return toLiveChatAvailability(await res.json());
    } catch {
      return toLiveChatAvailability(null);
    }
  }

  /** Forward a customer message to the live human (no agent stream expected). */
  async sendHandoffMessage(text: string): Promise<void> {
    // TRAC-1198: the message is the end of this bit of typing; don't flag it on the next poll.
    this._customerTypedSincePoll = false;
    // Card/SSN scrub at the network boundary — the customer's message to a live
    // rep is gone of any payment card before it leaves the browser. Mirrors the
    // backend redact_critical (shared/pii.py); backend stays the guarantee.
    text = safeRedact(text).text; // CPOC-140: fails closed to "[message withheld]"
    // TRAC-732: accept a *pending* session (loadAnySessionId) — a handoff is
    // valid before any message is sent.
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    // TRAC-1089: the rep's session is owner-checked like any other, so the key rides along.
    const res = await fetch(`${getBackendApiUrl()}/agent/user-message`, {
      method: 'POST',
      headers: await this.getRequestHeaders(sessionId),
      body: JSON.stringify({ prompt: text, runtimeSessionId: sessionId }),
    });
    // A refused or failed send must not show as "sent · waiting for the rep".
    if (!res.ok) {
      throw new Error(`Handoff message failed: ${res.status}`);
    }
  }

  /** Poll for handoff events (human messages + lifecycle) while a handoff is active.
   *  Polling — not the per-turn SSE stream — because a human reply can arrive
   *  minutes after any agent stream has closed. */
  startHandoffListener(callbacks: {
    onHumanMessage: (text: string, agentName: string, html?: string) => void;
    onStatus: (state: string, agentName?: string, agentAvatar?: string, agentId?: string, agentEmployeeId?: string, reason?: string) => void;
  }): void {
    // TRAC-732: accept a *pending* session (loadAnySessionId) — a handoff is
    // valid before any message is sent.
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return;
    this.stopHandoffListener();
    this._journeySession = sessionId;
    this._sendJourneySoon();
    const holder = this as unknown as {
      _handoffPoll?: ReturnType<typeof setTimeout> | null;
      _handoffSince?: number;
      _handoffStop?: boolean;
      _handoffInFlight?: boolean;
      _handoffSeen?: Set<number>;
      _handoffRun?: object;
      _handoffSlowPending?: boolean;
    };
    // TRAC-732 resume (#15): start the cursor at the last event the customer already saw
    // (passed by a resume-on-load), so a refresh/navigation catches up from there rather than
    // replaying the whole handoff. Defaults to 0 for a fresh handoff.
    holder._handoffSince = this._handoffResumeSince ?? 0;
    holder._handoffStop = false;
    holder._handoffInFlight = false;
    // Dedup by absolute event index. The event log is an append-only list; the i-th event of a
    // batch fetched at since=S sits at index S+i. Two overlapping polls (or a resume overlap, or
    // an LP redelivery) fetch the same index — we render each index at most once, killing the
    // "rep said hello, UI shows it twice" duplicates from ANY source. (TRAC-732)
    holder._handoffSeen = new Set<number>();
    const poll = async () => {
      if (holder._handoffStop) return;
      if (holder._handoffInFlight) return;   // single-flight: never overlap two polls
      holder._handoffInFlight = true;
      const sinceAtStart = holder._handoffSince || 0;
      try {
        const headers = await this.getRequestHeaders(sessionId);
        // TRAC-732 intent model: the poll IS the heartbeat — carry the customer's current intent
        // so the backend can sever a handoff the customer has actually left (reconcile-or-sever).
        const cur = this._readIntent();
        const typing = this._customerTypedSincePoll ? '&typing=1' : '';
        this._customerTypedSincePoll = false;
        // TRACI-848: bounded, so a hung request can never end the poll chain (and the heartbeat).
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), HANDOFF_POLL_TIMEOUT_MS);
        let res: Response;
        try {
          res = await fetch(
            `${getBackendApiUrl()}/agent/handoff-events/${sessionId}?since=${sinceAtStart}&intentSeq=${cur.seq}&wantsHuman=${cur.wantsHuman}${typing}`,
            { headers, signal: controller.signal },
          );
        } finally {
          clearTimeout(abortTimer);
        }
        if (res.ok) {
          const body = await res.json();
          // TRAC-732 intent model: the backend severed this handoff (the customer's latest intent
          // was to leave) — stop polling and reset the page to the AI.
          if (body.voided) {
            this.stopHandoffListener();
            callbacks.onStatus('voided');
            return;
          }
          // TRAC-732: the customer may have hit "leave" WHILE this request was in flight.
          // _handoffStop was only checked before the awaits, so a late response could still
          // dispatch a rep's message into a chat the customer had already left. Re-check now
          // and drop the whole batch — nothing from a left handoff may reach the page.
          if (holder._handoffStop) return;
          const events = (body.events || []) as Array<{ type: string; data?: Record<string, unknown> }>;
          events.forEach((ev, i) => {
            const idx = sinceAtStart + i;            // this event's absolute position in the log
            if (holder._handoffSeen!.has(idx)) return; // already rendered — skip the duplicate
            holder._handoffSeen!.add(idx);
            const d = (ev.data || {}) as Record<string, unknown>;
            if (ev.type === 'human_message') {
              // `html` is present only when the rep used the toolbar (bold/italic/
              // underline/lists/links); plain messages keep the plain-text path.
              callbacks.onHumanMessage(
                (d.text as string) || '',
                (d.agentName as string) || 'Tire Expert',
                typeof d.html === 'string' && d.html ? d.html : undefined,
              );
              // Read receipts: the message reached this device → ACCEPT now (rep sees a double
              // gray check); READ follows once it is actually on screen (see _flushReadReceipts).
              if (typeof d.seq === 'number') {
                void this._postHandoffAck(sessionId, [d.seq], 'ACCEPT');
                this._unreadRepSeqs.add(d.seq);
              }
            } else if (ev.type === 'handoff_status') {
              this._sendJourneySoon(); // CPOC-123: a rep who just joined gets the current journey
              callbacks.onStatus((d.state as string) || '', d.agentName as string | undefined, d.agentAvatar as string | undefined, d.agentId as string | undefined, d.agentEmployeeId as string | undefined, d.reason as string | undefined);
            } else if (ev.type === 'secure_form') {
              // TRACI-1012: the rep sent a LivePerson secure form. Shown over the chat, never as text.
              window.dispatchEvent(new CustomEvent(SECURE_FORM_EVENT, {
                detail: { ...d, reportSubmitted: (inv: string, sub: string) => this._postSecureFormSubmitted(sessionId, inv, sub, d.title as string | undefined) },
              }));
            }
          });
          holder._handoffSince = (body.total as number) ?? holder._handoffSince;
          this._flushReadReceipts(sessionId);
          // TRAC-732 #15: persist the cursor so a refresh/navigation resumes from here — the
          // customer has seen everything up to this index; the poll restarts past it.
          try { localStorage.setItem(STORAGE_KEY_HANDOFF_CURSOR, String(holder._handoffSince)); } catch { /* quota */ }
        }
      } catch (e) {
        logger.error('[handoff] poll error', e);
      } finally {
        holder._handoffInFlight = false;
      }
    };
    // TRACI-848: every 2 s while the chat is on screen (a rep's reply keeps today's latency in
    // every state), every 5 s while it is off screen (tab hidden or the embed minimized), and
    // immediately when it comes back. Each run is timed from its start, like the old setInterval.
    // A tick still awaiting a poll from before a stop/restart must not start a second chain.
    const run = {};
    holder._handoffRun = run;
    const tick = async () => {
      holder._handoffPoll = null;
      holder._handoffSlowPending = false;
      const startedAt = Date.now();
      await poll();
      if (holder._handoffStop || holder._handoffRun !== run) return;
      const offScreen = this._chatOffScreen();
      const every = offScreen ? HANDOFF_POLL_OFF_SCREEN_MS : HANDOFF_POLL_ON_SCREEN_MS;
      holder._handoffSlowPending = offScreen;
      holder._handoffPoll = setTimeout(tick, Math.max(0, every - (Date.now() - startedAt)));
    };
    // Back on screen: poll now rather than waiting out the slow off-screen delay. Only a pending
    // SLOW poll is pulled forward: the observer also fires on every content-height change of an
    // open chat, which must not add polls. A poll in flight reschedules itself when it finishes.
    this._onHandoffBackOnScreen = () => {
      if (!holder._handoffSlowPending) return;
      if (holder._handoffStop || holder._handoffInFlight || this._chatOffScreen()) return;
      if (holder._handoffPoll) { clearTimeout(holder._handoffPoll); holder._handoffPoll = null; }
      void tick();
    };
    // Read receipts: a customer who tabs away and comes back has only now READ what arrived
    // meanwhile — that transition is the signal the rep cares about ("seen it and went quiet"
    // vs "hasn't seen it"). Also covers the panel being re-opened, via the per-poll re-check.
    this._onVisibilityForReceipts = () => {
      this._flushReadReceipts(sessionId);
      this._onHandoffBackOnScreen?.();
    };
    try { document.addEventListener('visibilitychange', this._onVisibilityForReceipts); } catch { /* non-DOM */ }
    // Re-opening a minimized embed lays the iframe out again; a ResizeObserver sees that (a
    // window resize event does not fire for display:none -> block).
    try {
      if (typeof ResizeObserver !== 'undefined') {
        const onLayout = this._onHandoffBackOnScreen;
        this._handoffLayoutObserver = new ResizeObserver(() => onLayout());
        this._handoffLayoutObserver.observe(document.documentElement);
      }
    } catch { /* non-DOM */ }
    void tick(); // immediate first poll
  }

  // ---- Read receipts (AcceptStatusEvent back to LivePerson) ---------------------------------
  private _unreadRepSeqs = new Set<number>();
  private _onVisibilityForReceipts: (() => void) | null = null;
  /** TRACI-848: polls at once when the chat comes back on screen (tab shown, embed re-opened). */
  private _onHandoffBackOnScreen: (() => void) | null = null;
  private _handoffLayoutObserver: ResizeObserver | null = null;
  /** TRACI-848: this document has been laid out at a real size at least once. */
  private _chatEverLaidOut = false;
  /** TRAC-1198: the customer typed since the last handoff poll. */
  private _customerTypedSincePoll = false;

  /**
   * TRAC-1198: the customer is typing to a live rep. Rides on the next handoff-events poll
   * (every 2 s while the chat is on screen) as `typing=1`, so it costs no extra request and is throttled by the poll
   * itself; the backend forwards it as a typing event (Connect only, at most one per 3 s).
   * Never from a prod build.
   */
  noteCustomerTyping(): void {
    if (isProdBuild()) return;
    this._customerTypedSincePoll = true;
  }

  /** The customer can actually see the chat: tab in the foreground AND the panel laid out.
   *  A minimized embed hides the iframe (display:none), which leaves its document "visible" but
   *  with zero-size layout — so size is the panel-open signal. */
  /** TRACI-848: nobody can see the chat — the tab is hidden, or the embed is minimized (the
   *  loader sets the panel display:none, which lays this document out at 0x0, as _chatOnScreen
   *  reads it). 0x0 counts only once the document has been laid out at a real size, so a
   *  document that never lays out (jsdom) or starts minimized keeps the 2 s cadence. */
  private _chatOffScreen(): boolean {
    try {
      if (typeof document === 'undefined') return false;
      if (document.visibilityState === 'hidden') return true;
      const r = document.documentElement.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        this._chatEverLaidOut = true;
        return false;
      }
      return this._chatEverLaidOut;
    } catch { return false; }
  }

  private _chatOnScreen(): boolean {
    try {
      if (typeof document === 'undefined') return false;
      if (document.visibilityState !== 'visible') return false;
      const r = document.documentElement.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    } catch { return false; }
  }

  private _flushReadReceipts(sessionId: string): void {
    if (this._unreadRepSeqs.size === 0 || !this._chatOnScreen()) return;
    const seqs = [...this._unreadRepSeqs];
    this._unreadRepSeqs.clear();
    void this._postHandoffAck(sessionId, seqs, 'READ');
  }

  /** TRACI-1012: LivePerson's secure form was submitted. Only LivePerson's ids, never card data. */
  private async _postSecureFormSubmitted(sessionId: string, invitationId: string, submissionId: string, title?: string): Promise<void> {
    try {
      const headers = await this.getRequestHeaders(sessionId);
      await fetch(`${getBackendApiUrl()}/agent/secure-form-submitted/${sessionId}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitationId, submissionId, title }),
      });
    } catch { /* best-effort */ }
  }

  private async _postHandoffAck(sessionId: string, seqs: number[], status: 'ACCEPT' | 'READ'): Promise<void> {
    try {
      const headers = await this.getRequestHeaders(sessionId);
      await fetch(`${getBackendApiUrl()}/agent/handoff-ack/${sessionId}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ seqs, status }),
      });
    } catch { /* best-effort: a missed receipt is cosmetic */ }
  }

  /** TRAC-1006: sessionStorage, or null where the browser refuses it (partitioned iframe). */
  private _tabStorage(): Storage | null {
    try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
  }

  /**
   * TRAC-1006: the persisted handoff attempt for the CURRENT conversation, read
   * synchronously so ChatInterface can seed its state before the first
   * announcement poll result is applied. Null when none, or when it belongs to a
   * different session.
   */
  loadHandoffAttempt(): HandoffAttempt | null {
    return readHandoffAttempt(this._tabStorage(), STORAGE_KEY_HANDOFF_ATTEMPT, this.agentSessionId || this.loadAnySessionId());
  }

  /**
   * TRAC-1049: is there an ask for a person open on this conversation according to the record
   * every tab shares (`traci_handoff_funnel`, localStorage)?
   *
   * The per-tab record above is the only thing that used to keep the resume check going past its
   * backoff, and a SECOND TAB never has one. So a tab whose first four checks all failed stopped
   * asking for good: no handoff state, no waiting band, no Leave Queue and no still-trying line,
   * on a conversation that was very much in a queue. That is the second half of TRAC-1049.
   *
   * Bounded by HANDOFF_OPEN_ATTEMPT_FRESH_MS. The shared record is cleared when a rep joins or
   * the attempt ends, but deliberately KEPT when a page is left mid-wait, so without a bound a
   * tab opened the next morning would poll a dead endpoint all day.
   */
  private _sharedHandoffAttemptOpen(): boolean {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    let store: Storage | null = null;
    try { store = typeof localStorage === 'undefined' ? null : localStorage; } catch { store = null; }
    const open = readOpenAttempt(store, OPEN_ATTEMPT_STORAGE_KEY, sessionId);
    return !!open && Date.now() - open.askedAt < HANDOFF_OPEN_ATTEMPT_FRESH_MS;
  }

  /** TRAC-1006: persist the attempt against the current session (no-op before one exists). */
  saveHandoffAttempt(fields: Omit<HandoffAttemptFields, 'sessionId'>): void {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return;
    writeHandoffAttempt(this._tabStorage(), STORAGE_KEY_HANDOFF_ATTEMPT, { sessionId, ...fields });
  }

  /**
   * One ask of /agent/handoff-state. Null on ANY failure — network, 5xx, a firewall 403, or no
   * answer within HANDOFF_STATE_TIMEOUT_MS. The timeout matters: a request the firewall silently
   * drops would otherwise hang for the browser's own timeout and stretch a 1/2/4/8/15 s retry
   * schedule into minutes. Same figure as the announcement poll's FETCH_TIMEOUT_MS.
   */
  private async _fetchHandoffState(sessionId: string, timeoutMs = 8000): Promise<{ active: boolean; mode: string; agentName: string | null; total: number } | null> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const headers = await this.getRequestHeaders(sessionId);
      // TRAC-732 intent model: report our current intent on load. If the customer's latest intent
      // was to leave, the backend severs any lingering handoff and returns active:false — a reload
      // can't resume a chat the customer had already left.
      const cur = this._readIntent();
      const res = await fetch(`${getBackendApiUrl()}/agent/handoff-state/${sessionId}?intentSeq=${cur.seq}&wantsHuman=${cur.wantsHuman}`, { headers, signal: controller.signal });
      if (!res.ok) {
        logger.warn(`[handoff] resume check answered ${res.status}`);
        return null;
      }
      return await res.json() as { active: boolean; mode: string; agentName: string | null; total: number };
    } catch (e) {
      logger.warn('[handoff] resume check failed', e);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * TRAC-732 #15: on page load, ask the backend whether this session is mid-handoff. A refresh
   * OR a navigation (the embedded widget is rebuilt on every page) must resume the live chat,
   * not drop the customer into a split-brain where their messages still reach the rep but the
   * rep's replies never render. Returns the snapshot so the caller can rehydrate the band/state;
   * when active it also (re)starts the poll from the customer's persisted cursor so only events
   * missed during the reload are fetched (the dedup set + cursor prevent any replay).
   *
   * TRAC-1006: an unanswered check is retried instead of returning null once. Backoff 1, 2, 4,
   * 8 s, then every 15 s for as long as the persisted attempt says a handoff is still open
   * (resumeRetryDelayMs). Only a real answer — active or not — ends the loop early; null now
   * means "gave up" (nothing open to protect), "the caller went away" (`isCancelled`), or "the
   * conversation changed" (Clear conversation mid-retry — the answer would be about another chat).
   */
  async resumeHandoffIfActive(
    callbacks: {
      onHumanMessage: (text: string, agentName: string, html?: string) => void;
      onStatus: (state: string, agentName?: string, agentAvatar?: string, agentId?: string, agentEmployeeId?: string, reason?: string) => void;
    },
    opts: { isCancelled?: () => boolean } = {},
  ): Promise<{ active: boolean; mode: string; agentName: string | null; total: number } | null> {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return null;
    const stillThisChat = () => !opts.isCancelled?.() && (this.agentSessionId || this.loadAnySessionId()) === sessionId;
    for (let failures = 0; ; failures++) {
      if (!stillThisChat()) return null;
      const state = await this._fetchHandoffState(sessionId);
      if (!stillThisChat()) return null;
      if (state) return this._applyResumedHandoff(sessionId, state, callbacks);
      const delay = resumeRetryDelayMs(
        failures,
        handoffAttemptOpen(this.loadHandoffAttempt()) || this._sharedHandoffAttemptOpen(),
      );
      if (delay === null) {
        logger.warn(`[handoff] resume check gave up after ${failures + 1} attempts`);
        return null;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  /**
   * TRAC-1006: after the page HEARD a handoff end (a bridge ended / error / voided event), ask the
   * backend until it CONFIRMS the handoff is over. Resolves true on a confirmed end; null when it
   * gave up, the caller went away, or the conversation changed.
   *
   * Deliberately confirm-only. An `active` answer is "not confirmed yet", never a handoff to
   * resume: bridge.py broadcasts `ended` a moment before it flips the session's mode to ai, the
   * page's event cursor has already moved past that `ended`, and resuming there left the page
   * "active" on a dead conversation that no later event would ever close. Same retry schedule
   * as the resume check, so it holds up while the backend cannot be reached.
   */
  async confirmHandoffEnded(opts: { isCancelled?: () => boolean } = {}): Promise<true | null> {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return null;
    const stillThisChat = () => !opts.isCancelled?.() && (this.agentSessionId || this.loadAnySessionId()) === sessionId;
    for (let attempt = 0; ; attempt++) {
      if (!stillThisChat()) return null;
      const state = await this._fetchHandoffState(sessionId);
      if (!stillThisChat()) return null;
      if (state && !state.active) {
        try { localStorage.removeItem(STORAGE_KEY_HANDOFF_CURSOR); } catch { /* noop */ }
        return true;
      }
      const delay = resumeRetryDelayMs(
        attempt,
        handoffAttemptOpen(this.loadHandoffAttempt()) || this._sharedHandoffAttemptOpen(),
      );
      if (delay === null) return null;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  /**
   * TRACI-1092: the escalate fetch was rejected before the browser saw an answer ("Failed to
   * fetch" / "Load failed": a frame remount, a dropped connection), but the request may well
   * have reached the backend, which then started the handoff anyway. In 11 of 15 prod
   * handoff_failed in a week that is what happened: the page apologised while a rep joined.
   * Ask the backend instead of guessing: true once it reports a handoff waiting or live, false
   * when the window passes without one, null when cancelled (the customer left, or a
   * different chat took over).
   */
  async confirmHandoffStarted(opts: { isCancelled?: () => boolean; windowMs?: number; intervalMs?: number } = {}): Promise<boolean | null> {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return false;
    const windowMs = opts.windowMs ?? CONNECT_CONFIRM_WINDOW_MS;
    const intervalMs = opts.intervalMs ?? CONNECT_CONFIRM_INTERVAL_MS;
    const stillThisChat = () => !opts.isCancelled?.() && (this.agentSessionId || this.loadAnySessionId()) === sessionId;
    const deadline = Date.now() + windowMs;
    for (;;) {
      if (!stillThisChat()) return null;
      // Each check gets only what is left of the window, so a hung one cannot stretch it.
      const left = deadline - Date.now();
      if (left <= 0) return false;
      const state = await this._fetchHandoffState(sessionId, Math.min(left, CONNECT_CONFIRM_CALL_TIMEOUT_MS));
      if (!stillThisChat()) return null;
      if (state?.active) return true;
      if (Date.now() + intervalMs > deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  private _applyResumedHandoff(
    sessionId: string,
    state: { active: boolean; mode: string; agentName: string | null; total: number },
    callbacks: {
      onHumanMessage: (text: string, agentName: string, html?: string) => void;
      onStatus: (state: string, agentName?: string, agentAvatar?: string, agentId?: string, agentEmployeeId?: string, reason?: string) => void;
    },
  ): { active: boolean; mode: string; agentName: string | null; total: number } {
    if (!state.active) {
      // Handoff is over (ended/expired while away) — clear the stale cursor so a future
      // handoff starts clean, and let the caller render the closed state.
      try { localStorage.removeItem(STORAGE_KEY_HANDOFF_CURSOR); } catch { /* noop */ }
      return state;
    }
    // Resume from what the customer last saw; fall back to the server total if we have no
    // local cursor (e.g. the handoff was started in a different tab).
    const persisted = Number(localStorage.getItem(STORAGE_KEY_HANDOFF_CURSOR));
    this._handoffResumeSince = Number.isFinite(persisted) ? persisted : state.total;
    this.startHandoffListener(callbacks);
    this._handoffResumeSince = null; // consumed
    // TRACI-852: the shopper moved pages mid-chat; keep the rep's LP session on their cart.
    try { void keepLpCartLive().catch(() => { /* best-effort */ }); } catch { /* best-effort */ }
    // TRAC-732 #16: tell the rep the customer is still here (refreshed/navigated) and, when we
    // know it, what page they're on — so a reload doesn't look like a disappearance.
    try {
      const url = this._pageData?.page_url || this._pageData?.page_name || '';
      const qs = url ? `?url=${encodeURIComponent(String(url))}` : '';
      void this.getRequestHeaders(sessionId).then((headers) => fetch(`${getBackendApiUrl()}/agent/handoff-presence/${sessionId}${qs}`, {
        method: 'POST', headers,
      })).catch(() => { /* best-effort */ });
    } catch { /* noop */ }
    return state;
  }

  /**
   * TRACI-628: has a staff member sent this chat to a rep from the live chat monitor?
   * Taking the answer removes it on the server. False on any error: a missed poll is
   * retried by the next one, and a staff send is never worth a broken chat.
   */
  async checkStaffHandoff(sessionId: string): Promise<{ department: string | null } | null> {
    try {
      const headers = await this.getRequestHeaders(sessionId);
      const res = await fetch(`${getBackendApiUrl()}/agent/staff-handoff/${encodeURIComponent(sessionId)}`, {
        method: 'GET', headers, cache: 'no-store',
      });
      if (!res.ok) return null;
      const body = await res.json() as { send?: unknown; department?: unknown };
      if (body.send !== true) return null;
      // TRACI-628: the team staff chose; anything else lets the chat pick, as a shopper's press does.
      const dept = body.department === 'Sales' || body.department === 'Customer Care' ? body.department : null;
      return { department: dept };
    } catch {
      return null;
    }
  }

  /**
   * CPOC-123: send the shopper's current pages and cart to a live handoff (debounced). TRACI
   * forwards it only to a Connect rep who has joined and drops an unchanged one, so calling this
   * freely is cheap. Best-effort: a failure costs the rep a live update, never the chat.
   */
  private _sendJourneySoon(): void {
    if (!this._journeySession) return;
    if (this._journeyTimer) clearTimeout(this._journeyTimer);
    this._journeyTimer = setTimeout(() => {
      this._journeyTimer = null;
      const sessionId = this._journeySession;
      const journey = handoffJourney(import.meta.env.VITE_ENV || 'dev');
      if (!sessionId || !journey) return;
      void this.getRequestHeaders(sessionId).then((headers) => fetch(
        `${getBackendApiUrl()}/agent/handoff-presence/${sessionId}?event=journey`,
        { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ journey }) },
      )).catch(() => { /* best-effort */ });
    }, 1000);
  }

  stopHandoffListener(): void {
    // NOTE: this is also called internally at the top of startHandoffListener to reset, so it
    // must NOT clear the persisted resume cursor — that's done on a real END (endHandoff).
    this._journeySession = null;
    const holder = this as unknown as { _handoffPoll?: ReturnType<typeof setTimeout> | null; _handoffStop?: boolean };
    holder._handoffStop = true;
    if (holder._handoffPoll) { clearTimeout(holder._handoffPoll); holder._handoffPoll = null; }
    if (this._onVisibilityForReceipts) {
      try { document.removeEventListener('visibilitychange', this._onVisibilityForReceipts); } catch { /* noop */ }
      this._onVisibilityForReceipts = null;
    }
    if (this._handoffLayoutObserver) {
      try { this._handoffLayoutObserver.disconnect(); } catch { /* noop */ }
      this._handoffLayoutObserver = null;
    }
    this._onHandoffBackOnScreen = null;
    this._unreadRepSeqs.clear();
    this._customerTypedSincePoll = false;
  }

  /** Customer leaves the live chat — closes the LP socket on the backend. */
  /**
   * DEV/TEST ONLY (non-prod) — simulate a backend redeploy for this session by severing the
   * LivePerson bridge server-side while leaving the handoff mode alone. The chat should either
   * reconnect on the next poll (replaying anything the rep said meanwhile) or, with
   * dropResume=true, end cleanly into the survey. Nothing here changes local state: the whole
   * point is to watch the page react to the backend the way it would after a real deploy.
   */
  async severHandoffBridge(opts?: { dropResume?: boolean }): Promise<unknown> {
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) throw new Error('No active session');
    const params = new URLSearchParams();
    if (opts?.dropResume) params.set('dropResume', 'true');
    const res = await fetch(
      `${getBackendApiUrl()}/agent/dev/sever-handoff-bridge/${sessionId}?${params.toString()}`,
      { method: 'POST', headers: await this.getRequestHeaders(sessionId) },
    );
    if (!res.ok) throw new Error(`Sever failed (${res.status})`);
    return res.json();
  }

  endHandoff(opts?: { notifyAgentLeft?: boolean }): Promise<void> {
    closeSecureForm();  // TRACI-1012: a secure form never outlives its live chat
    // TRAC-1046: registered here, synchronously, rather than around the fetch
    // inside. resetHandoff() calls this without awaiting it and the QA
    // extension's New session asks whenClearSettled() on the very next line —
    // a request registered only after this method's own `await` would be missed,
    // and a missed end-handoff is a rep left ringing.
    return this._trackClearTeardown(this._endHandoff(opts));
  }

  private async _endHandoff(opts?: { notifyAgentLeft?: boolean }): Promise<void> {
    this.stopHandoffListener();
    try { localStorage.removeItem(STORAGE_KEY_HANDOFF_CURSOR); } catch { /* noop */ } // TRAC-732 #15: handoff over — no resume
    const sessionId = this.agentSessionId || this.loadAnySessionId();
    if (!sessionId) return;
    try {
      // TRAC-732: notifyAgentLeft=true (customer-initiated end) tells the backend
      // to send a "customer left the chat" note to the LP agent before closing.
      // intentSeq stamps this leave with a number > any prior connect, so a slower connect that
      // lands after it is recognized as stale and refused (the connect/leave race).
      const intentSeq = this._bumpIntent(false);
      const params = new URLSearchParams({ intentSeq: String(intentSeq) });
      if (opts?.notifyAgentLeft) params.set('notifyAgentLeft', 'true');
      await fetch(`${getBackendApiUrl()}/agent/end-handoff/${sessionId}?${params.toString()}`, {
        method: 'POST',
        headers: await this.getRequestHeaders(sessionId),
      });
    } catch (e) {
      logger.error('[handoff] end error', e);
    }
  }

  /**
   * Convert backend SSE event format to frontend event format
   */
  /**
   * Convert backend SSE event to frontend chunk format
   * @param event - The SSE event from the backend
   * @param skipReplayedContent - If true, skip replayed content/tool events (used during resume to prevent duplication)
   *                              If false, process all events (used during normal message flow where message ID filtering handles cross-message contamination)
   */
  convertBackendEventToFrontend(event: SSEEvent, skipReplayedContent: boolean = false): StreamChunk | null {
    if (!event || !event.type) {
      return null;
    }

    // Event types that should not be replayed during resume (already rendered from session storage)
    // These are only skipped when skipReplayedContent is true (resume flow)
    // During normal message flow (sendMessageAndStream), message ID filtering handles cross-message contamination
    const NON_REPLAYABLE_EVENT_TYPES = new Set([
      // Content events - prevents previous message text from leaking during resume (TRAC-203)
      'response_chunk',
      'response_start',
      'agent_thinking',
      // UI events
      'selection_cards',
      'source_cards',
      'ranking_cards',
      'rating_display',
      'tdg_link_card',
      'tire_browse_card',  // TRAC-295: unified tire-browse card (size / category / fallback)
      'size_search_link_card',  // TRAC-396 (legacy browse-by-size / staggered redirect)
      'vehicle_info_card',
      'tire_info_card',
      'tire_card',  // New unified tire card
      'tire_size_confirmation',  // Tire size confirmation card
      'human_handoff',  // Sales/Customer Care redirect card (TRAC-291)
      'navigation_card',  // navigate_site wayfinding page-link card
      // Tool events
      'tool_use_start',
      'tool_use_progress',
      'tool_use_complete',
      'tool_use_error'
    ]);

    // Skip replayed events only during resume flow (skipReplayedContent=true)
    // During normal message flow, message ID filtering in sendMessageAndStream handles cross-message contamination
    if (skipReplayedContent && (event as Record<string, unknown>).replayed && NON_REPLAYABLE_EVENT_TYPES.has(event.type)) {
      logger.debug(`[TRAC-203] convertEvent: Skipping replayed ${event.type} (skipReplayedContent=true, seq=${event.sequence})`);
      return null;
    }

    // Map backend event types to frontend types
    switch (event.type) {
      case 'response_start':
        return {
          type: 'content',
          content: '',
        };

      case 'response_chunk':
        return {
          type: 'content',
          content: (event.data?.chunk as string) || '',
          sequence: event.sequence
        };

      case 'tool_use_start':
        logger.debug('DEBUG tool_use_start event:', JSON.stringify(event, null, 2));
        return {
          type: 'tool_use_start',
          toolId: (event.data?.toolId as string) || `tool_${Date.now()}`,
          toolName: (event.data?.toolName as string) || 'unknown',
          // TRAC-732: the orchestrator emits the tool input under `toolInput` on
          // tool_use_complete (and `parameters` on tool_use_start). Read toolInput
          // first so route_to_human's `department`/`escalation` actually reach the UI.
          parameters: (event.data?.toolInput as Record<string, unknown>)
            || (event.data?.parameters as Record<string, unknown>) || {},
          sequence: event.sequence  // TRAC-203: Include sequence for startSequence tracking
        };

      case 'tool_use_progress':
        return {
          type: 'tool_progress',
          toolId: (event.data?.toolId as string) || 'unknown',
          toolName: (event.data?.toolName as string) || 'unknown',
          status: (event.data?.status as string) || 'Processing'
        };

      case 'tool_use_complete':
        logger.debug('DEBUG tool_use_complete event:', JSON.stringify(event, null, 2));
        return {
          type: 'tool_use_complete',
          toolId: (event.data?.toolId as string) || 'unknown',
          toolName: (event.data?.toolName as string) || 'unknown',
          // TRAC-732: prefer toolInput (see tool_use_start note) so a tool's
          // structured input reaches the UI on completion.
          parameters: (event.data?.toolInput as Record<string, unknown>)
            || (event.data?.parameters as Record<string, unknown>) || {},
          resultSummary: (event.data?.resultSummary as string) || 'Completed'
        };

      case 'tool_use_error':
        return {
          type: 'tool_error',
          toolId: (event.data?.toolId as string) || 'unknown',
          toolName: (event.data?.toolName as string) || 'unknown',
          error: (event.data?.error as string) || 'Unknown error'
        };

      case 'response_complete':
        // Wire field: 'langfuseTraceUrl' (camelCase, matches backend api.py
        // build_response_complete_data). If renaming, update both sites.
        // Server-side gates emission on auth_user.is_internal; this extract
        // is pass-through only.
        return {
          type: 'done',
          sessionHash: (event.data?.sessionHash as string) || undefined,
          traceId: (event.data?.traceId as string) || undefined,
          langfuseTraceUrl: (event.data?.langfuseTraceUrl as string) || undefined,
        };

      case 'response_error':
        return {
          type: 'error',
          error: (event.data?.error as string) || 'Unknown error',
          // TRAC-402: Pass through session_auto_cleared flag so the UI can show
          // a user-friendly "session reset" notice instead of a raw error bubble.
          data: event.data?.session_auto_cleared ? { session_auto_cleared: true } : undefined,
        };

      case 'trace_context':
        // Trace context for Datadog feedback correlation
        // Pass through the data object containing feedback_id
        return {
          type: 'trace_context',
          data: {
            feedback_id: (event.data?.feedback_id as string) || '',
          },
        };

      case 'agent_thinking':
        return {
          type: 'agent_thinking',
          status: (event.data?.status as string) || 'Processing your request...'
        };

      case 'heartbeat':
      case 'connected':
        // Ignore these events
        return null;

      case AGENT_KEEPALIVE_EVENT:
        // TRAC-862: no UI effect — it must not clear the thinking state or land
        // in the transcript. Deliberately a SEPARATE case from the two above:
        // those are connection-level and get dropped before they can reset the
        // idle watchdog, this one is the agent proving it is still working and
        // MUST reset it. Do not merge these cases.
        return null;

      // UI Event Types (from UI hooks system)
      case 'selection_cards':
        return {
          type: 'selection_cards',
          title: (event.data?.title as string) || undefined,
          options: event.data?.options as Array<{ label: string; value: string; subtitle?: string }> || [],
          multiSelect: (event.data?.multiSelect as boolean) || false,
          hint: (event.data?.hint as string) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'source_cards':
        return {
          type: 'source_cards',
          title: (event.data?.title as string) || undefined,
          options: event.data?.options as Array<{ label: string; value: string; subtitle?: string }> || [],
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'ranking_cards':
        return {
          type: 'ranking_cards',
          title: (event.data?.title as string) || undefined,
          options: event.data?.options as Array<{ label: string; value: string; subtitle?: string }> || [],
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'rating_display':
        return {
          type: 'rating_display',
          title: (event.data?.title as string) || undefined,
          overallRating: (event.data?.overallRating as number) || undefined,
          reviewCount: (event.data?.reviewCount as number) || undefined,
          metrics: event.data?.metrics as Array<{ label: string; subtitle: string }> || [],
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'tdg_link_card':
        return {
          type: 'tdg_link_card',
          title: (event.data?.title as string) || undefined,
          tdgUrl: (event.data?.tdgUrl as string) || undefined,
          vehicle: event.data?.vehicle as { description?: string; tireSize?: string; frontTireSize?: string; rearTireSize?: string; season?: string; make?: string; model?: string; year?: number; variant?: string } || undefined,
          priorities: event.data?.priorities,
          indicators: event.data?.indicators as Array<{ type: string; label: string; description?: string }> || [],
          ctaText: (event.data?.ctaText as string) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      // TRAC-295: tire_browse_card — the single card for ANY tire-browse link from
      // find_tires, whatever filtered it (size search, category page, or no-results
      // fallback). The backend computes a human `title`; the card renders it. (Replaces
      // the old size_search_link_card / category_browse_card split — one card for all.)
      case 'tire_browse_card':
        return {
          type: 'tire_browse_card',
          searchUrl: (event.data?.url as string) || undefined,
          title: (event.data?.title as string) || undefined,
          tireSize: (event.data?.size as string) || undefined,
          ctaText: (event.data?.ctaText as string) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      // TRAC-396: size_search_link_card — emits a rich card with the size + CTA button
      // in place of the bare inline link the "Browse tires by size" flow used to produce.
      case 'size_search_link_card':
        return {
          type: 'size_search_link_card',
          tireSize: (event.data?.tireSize as string) || undefined,
          searchUrl: (event.data?.searchUrl as string) || undefined,
          ctaText: (event.data?.ctaText as string) || undefined,
          // Staggered fields (TRAC-396 follow-up). When set, the card renders
          // Front/Rear stacked. Driven by validate_tire_size's is_staggered
          // result so the URL shape and card layout stay in sync.
          isStaggered: (event.data?.isStaggered as boolean) || undefined,
          frontTireSize: (event.data?.frontTireSize as string) || undefined,
          rearTireSize: (event.data?.rearTireSize as string) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'vehicle_info_card':
        return {
          type: 'vehicle_info_card',
          title: (event.data?.title as string) || undefined,
          vehicle: event.data?.vehicle as {
            make?: string;
            model?: string;
            year?: number;
            variant?: string;
            tireSize?: string;
            staggered?: boolean;
            frontSize?: string | null;
            rearSize?: string | null;
            runflat?: boolean;
          } || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'tire_info_card':
        return {
          type: 'tire_info_card',
          title: (event.data?.title as string) || undefined,
          tire: event.data?.tire as {
            modelName?: string;
            manufacturer?: string;
            tireType?: string;
            sizesAvailable?: string[];
            specs?: Record<string, unknown>;
          } || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'tire_card':
        return {
          type: 'tire_card',
          title: (event.data?.title as string) || undefined,
          tire: event.data?.tire as {
            modelName?: string;
            manufacturer?: string;
            tireType?: string;
            sizesAvailable?: string[];
            productUrl?: string;
            specs?: Record<string, unknown>;
            priceRange?: string;
            price?: string;
            overallRating?: number;
            reviewCount?: number;
            ratingMetrics?: Array<{ label: string; score?: number }>;
          } || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      case 'ymmv_interface':
        return {
          type: 'ymmv_interface',
          title: (event.data?.title as string) || undefined,
          context: (event.data?.context as string) || undefined,
          prefill: (event.data?.prefill as string) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
          // Slice 2 resume: carry the intent the agent tagged the picker with so the completed
          // pick can POST a selectorCompletion that resumes the SAME capability (agentv2.resume).
          intent: (event.data?.intent as string) || undefined,
          // Slice 2 fitment: a check_fitment picker carries the size the customer ALREADY named
          // ("does 265/40R21 fit my Civic"). The picker then only needs the VEHICLE — carry the
          // size through so the tire-size step is skipped and the resume checks THAT size.
          prefillSize: (event.data?.prefillSize as string) || undefined,
        };

      case 'tire_size_confirmation':
        return {
          type: 'tire_size_confirmation',
          vehicleDescription: (event.data?.vehicleDescription as string) || undefined,
          tireSize: (event.data?.tireSize as string) || undefined,
          frontSize: (event.data?.frontSize as string) || undefined,
          rearSize: (event.data?.rearSize as string) || undefined,
          staggered: (event.data?.staggered as boolean) || false,
          isFactory: (event.data?.isFactory as boolean) || false,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      // TRAC-291 → TRAC-732: human_handoff is a CONTROL event, not a rendered card —
      // the stream loop reads escalation ("offer" → soft-handoff countdown card,
      // "immediate" → connect now) and department; the visible card is a locally
      // appended soft_handoff part. The old contact-card fields (phone/hours/…)
      // rendered nowhere and were dropped from the event on 2026-08-27.
      case 'human_handoff':
        return {
          type: 'human_handoff',
          department: (event.data?.department as string) || 'Customer Care',
          sourceTool: (event.data?.sourceTool as string) || undefined,
          escalation: (event.data?.escalation as string) || undefined,
          // TRAC-1212: who started it (shopper_asked_ai | ai_offered), for the funnel records.
          initiatedBy: (event.data?.initiatedBy as string) || undefined,
        };

      // navigate_site: wayfinding page-link card. The leaf emits the destination as
      // {url, title, when_to_use, type, tier}; map snake_case `when_to_use`/`type` to the
      // camelCase fields the renderer reads (whenToUse/navType) so they don't collide with
      // the part's own `type`.
      case 'navigation_card':
        return {
          type: 'navigation_card',
          url: (event.data?.url as string) || undefined,
          title: (event.data?.title as string) || undefined,
          whenToUse: (event.data?.when_to_use as string) || undefined,
          navType: (event.data?.type as string) || undefined,
          tier: (event.data?.tier as number) || undefined,
          sourceTool: (event.data?.sourceTool as string) || undefined,
        };

      // TRAC-147: Metrics event includes sessionHash for frontend sync
      case 'metrics':
        return {
          type: 'metrics',
          sessionHash: (event.data?.sessionHash as string) || undefined,
        };

      default:
        logger.warn('Unknown event type:', event.type);
        return null;
    }
  }

}

// Export singleton instance
export const chatAgentService = new RealChatAgentService();
export default chatAgentService;
